/**
 * metre-selector.js
 * Shows a metre dropdown on ALL products EXCEPT those tagged "standard-qty".
 * Works on the product page AND in quick-view modals.
 * Max metres = product's current stock (inventory_quantity).
 * If inventory tracking is off or stock is 0, falls back to DEFAULT_MAX.
 *
 * Install:
 *   1. Upload to Shopify Theme → Assets
 *   2. Add to theme.liquid just before </body>:
 *      <script src="{{ 'metre-selector.js' | asset_url }}" defer></script>
 *   3. Tag non-metre products (blouses etc.) with: standard-qty
 */

(function () {
  "use strict";

  // ── Inject Buy-it-now button CSS once ──────────────────────────────────────
  // Uses the same CSS custom properties the Halo/Shopify theme exposes for its
  // own buttons so the font, radius and spacing match exactly.
  // Hover turns the button solid black — same behaviour as the theme's outline buttons.
  (function injectBuyNowCSS() {
    if (document.getElementById("mw-buynow-styles")) return;
    const style = document.createElement("style");
    style.id = "mw-buynow-styles";
    style.textContent = `
      .mw-buynow {
        display: block !important;
        width: 100% !important;
        margin-top: 10px !important;
        /* Match theme button padding — use --buttons-border-width variable if set */
        padding: 15px 30px !important;
        box-sizing: border-box !important;

        /* Colours: transparent bg + foreground border (matches Shopify outline-button) */
        background-color: transparent !important;
        color: var(--color-foreground, #232323) !important;
        border-width: var(--buttons-border-width, 1px) !important;
        border-style: solid !important;
        border-color: var(--color-foreground, #232323) !important;
        border-radius: var(--buttons-radius, 0px) !important;

        /* Typography — inherit theme button font stack */
        font-family: var(--font-body-family, var(--font-heading-family, inherit)) !important;
        font-size: var(--font-body-size, 1.4rem) !important;
        font-weight: var(--font-body-weight, 500) !important;
        font-style: var(--font-body-style, normal) !important;
        letter-spacing: var(--font-body-letter-spacing, 0.1em) !important;
        text-transform: uppercase !important;
        text-align: center !important;
        line-height: 1 !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        cursor: pointer !important;

        /* Smooth hover transition — same as theme */
        transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
      }

      .mw-buynow:hover,
      .mw-buynow:focus-visible {
        background-color: var(--color-foreground, #000000) !important;
        color: var(--color-background, #ffffff) !important;
        border-color: var(--color-foreground, #000000) !important;
        outline: none !important;
      }

      .mw-buynow:active {
        opacity: 0.85 !important;
      }

      .mw-buynow:disabled,
      .mw-buynow[disabled] {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
      }

      /* ── Cart pcs quantity widget ─────────────────────────────────────────── */
      .mw-pcs-qty {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        gap: 0 !important;
        margin: 6px 0 !important;
        width: fit-content !important;
        border: 1px solid #ccc !important;
        box-sizing: border-box !important;
      }
      .mw-pcs-qty button[data-mw-minus],
      .mw-pcs-qty button[data-mw-plus] {
        display: inline-flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: center !important;
        flex-shrink: 0 !important;
        width: 32px !important;
        min-width: 32px !important;
        height: 32px !important;
        border: none !important;
        background: #fff !important;
        color: #333 !important;
        cursor: pointer !important;
        font-size: 18px !important;
        line-height: 1 !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
        -webkit-appearance: none !important;
        appearance: none !important;
      }
      .mw-pcs-qty input[data-mw-qty] {
        display: inline-block !important;
        flex-shrink: 0 !important;
        width: 40px !important;
        height: 32px !important;
        border: none !important;
        border-left: 1px solid #ccc !important;
        border-right: 1px solid #ccc !important;
        text-align: center !important;
        font-size: 14px !important;
        -moz-appearance: textfield !important;
        padding: 0 !important;
        margin: 0 !important;
        background: #fff !important;
        color: #333 !important;
        box-shadow: none !important;
      }
      .mw-pcs-qty input[data-mw-qty]::-webkit-outer-spin-button,
      .mw-pcs-qty input[data-mw-qty]::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
      }
    `;
    document.head.appendChild(style);
  })();

  // Products with either tag get the native integer quantity input (no metre dropdown).
  // "by-piece" is the current tag; "standard-qty" is kept for backwards compatibility.
  // Products with any of these tags get the native integer quantity input (no metre dropdown).
  // Stored lowercase — comparison is always case-insensitive (see check below).
  const EXCLUDE_TAGS = new Set(["pcs", "by-piece", "standard-qty"]);
  const DEFAULT_MAX = 100; // fallback if inventory tracking is off
  const MIN = 1;
  const STEP = 0.5;
  const NET_LEN_KEY = "properties[Net length]";

  // Per-handle product data cache (supports multiple products on same page via quick view)
  const productCache = {};

  // Map of injected form → { select, netLen } for fetch interceptor
  const formRefs = new Map();

  // Log all Fastr/Shiprocket global functions so we can find the cart-mode handler
  setTimeout(() => {
    const fastrFns = Object.getOwnPropertyNames(window).filter(
      (k) =>
        /shiprocket|fastrr|pickrr|buyCart|checkout/i.test(k) &&
        typeof window[k] === "function",
    );
    console.log("[metre-selector] Fastr window functions:", fastrFns);
  }, 2000);

  // ── Intercept window.shiprocketCheckoutBuyProductHandler ──────────────────
  // buyProduct() always calls this with qty=1 (Fastr's internal default).
  // We patch it at the call site so the checkout overlay sees the correct qty.
  // Two cases:
  //   A) shopify.js loaded before our defer script → property already exists;
  //      wrap it immediately and install a getter/setter to catch re-assignments.
  //   B) shopify.js loads after us → setter catches it when Fastr assigns it.
  (function _interceptFastrHandler() {
    // _h holds the current (possibly wrapped) handler; getter always reads it.
    let _h = null;

    function _wrap(origFn) {
      return function (...args) {
        // args[0] is the click Event — Fastr reads product data from the
        // .shiprocket-headless element's data-* attributes, not from the args.
        let correctQty = null;
        for (const [, refs] of formRefs) {
          if (!refs.select) continue;
          correctQty = Math.round((parseFloat(refs.select.value) || 1) / 0.5);
          break;
        }

        // Log the .shiprocket-headless dataset so we can see what Fastr reads
        const shEl = document.querySelector(".shiprocket-headless");
        if (shEl) {
          console.log(
            "[metre-selector] .shiprocket-headless dataset:",
            JSON.stringify(shEl.dataset),
          );
        }

        if (correctQty) {
          // Patch every .shiprocket-headless element's quantity/price attributes
          // before the handler reads them to build the checkout overlay
          document.querySelectorAll(".shiprocket-headless").forEach((el) => {
            el.setAttribute("data-quantity", String(correctQty));
            if (window.__mwCartLine?.price) {
              const priceRs = Math.round(window.__mwCartLine.price / 100); // paise → rupees
              el.setAttribute("data-price", String(priceRs));
              el.setAttribute("data-selling-price", String(priceRs));
              el.setAttribute("data-mrp", el.getAttribute("data-mrp") || "");
            }
          });
          console.log(
            "[metre-selector] .shiprocket-headless data-quantity →",
            correctQty,
          );
        }

        return origFn.apply(this, args);
      };
    }

    // Case A: already defined before our script ran
    if (typeof window.shiprocketCheckoutBuyProductHandler === "function") {
      _h = _wrap(window.shiprocketCheckoutBuyProductHandler);
      console.log(
        "[metre-selector] shiprocketCheckoutBuyProductHandler — wrapped existing ✓",
      );
    }

    try {
      Object.defineProperty(window, "shiprocketCheckoutBuyProductHandler", {
        configurable: true,
        enumerable: true,
        get: function () {
          return _h;
        }, // always expose the wrapper
        set: function (fn) {
          // Case B: Fastr assigns later
          console.log(
            "[metre-selector] shiprocketCheckoutBuyProductHandler — new assignment intercepted ✓",
          );
          _h = _wrap(fn);
        },
      });
    } catch (e) {
      // Property is non-configurable — fall back to direct reassignment
      console.warn(
        "[metre-selector] defineProperty failed, using direct wrap:",
        e.message,
      );
      if (typeof window.shiprocketCheckoutBuyProductHandler === "function") {
        window.shiprocketCheckoutBuyProductHandler = _wrap(
          window.shiprocketCheckoutBuyProductHandler,
        );
      }
      // Poll for late definition
      const _poll = setInterval(function () {
        if (
          typeof window.shiprocketCheckoutBuyProductHandler === "function" &&
          !window.shiprocketCheckoutBuyProductHandler.__mwWrapped
        ) {
          const orig = window.shiprocketCheckoutBuyProductHandler;
          const wrapped = _wrap(orig);
          wrapped.__mwWrapped = true;
          window.shiprocketCheckoutBuyProductHandler = wrapped;
          clearInterval(_poll);
          console.log(
            "[metre-selector] shiprocketCheckoutBuyProductHandler — polled wrap ✓",
          );
        }
      }, 200);
      setTimeout(function () {
        clearInterval(_poll);
      }, 10000);
    }
  })();

  // Allow only ONE non-modal product form per page load to prevent injecting
  // into sticky bars, related-product quick-adds, etc.
  let nonModalFormDone = false;

  // Track modal containers that already have a dropdown.
  // We stamp a data attribute on the container element itself so that if the theme
  // reuses the same container DOM node for a different product (clears + refills it),
  // we detect the new form has no existing dropdown and allow re-injection.

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Force an element (and all its ancestors up to `boundary`) to be visible.
   *
   * Two-step approach:
   *   1. Remove any inline display:none  (handles Liquid-rendered style="display:none")
   *   2. Re-check getComputedStyle — if still none, apply display:X !important
   *      (handles CSS-class hiding like .halo-qty-hidden { display:none !important })
   *
   * Uses a sensible display value per element type so layout isn't broken.
   */
  function forceShow(el, boundary) {
    if (!el) return;
    let n = el;
    while (n && n !== boundary) {
      // Step 1 — remove inline style
      n.style.removeProperty("display");
      // Step 2 — override CSS-class hiding with !important if still hidden
      if (window.getComputedStyle(n).display === "none") {
        const tag = (n.tagName || "").toLowerCase();
        const disp =
          tag === "input" || tag === "button"
            ? "inline-block"
            : tag === "quantity-input" || tag === "quantity-quick-view-input"
              ? "flex"
              : tag === "span" || tag === "label"
                ? "inline-block"
                : "block";
        n.style.setProperty("display", disp, "important");
      }
      n = n.parentElement;
    }
  }

  function getProductHandle() {
    const m = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    const res = await fetch(`/products/${handle}.js`);
    const data = await res.json();
    productCache[handle] = data;
    return data;
  }

  const STOCK_API = "https://checkout-api-zeta.vercel.app/api/product-stock";
  const stockCache = {};

  async function fetchAdminStock(handle, variantId) {
    const key = `${handle}:${variantId}`;
    if (stockCache[key] !== undefined) return stockCache[key];
    try {
      const url = `${STOCK_API}?handle=${handle}&variant_id=${variantId}`;
      const r = await fetch(url);
      if (!r.ok) return null;
      const d = await r.json();
      const qty = d.inventory_quantity ?? null;
      stockCache[key] = qty;
      return qty;
    } catch {
      return null;
    }
  }

  // Words that look like handles but are actually UI/structural element names.
  // If handle detection returns one of these, we reject it.
  const FAKE_HANDLES = new Set([
    "popup",
    "modal",
    "overlay",
    "backdrop",
    "drawer",
    "sidebar",
    "quick",
    "quickview",
    "quick-view",
    "lightbox",
    "dialog",
  ]);

  function isValidHandle(h) {
    return (
      h &&
      typeof h === "string" &&
      !h.includes("/") &&
      !h.includes(" ") &&
      h.length > 1 &&
      !FAKE_HANDLES.has(h.toLowerCase())
    );
  }

  /**
   * Try to find a product handle from a form's context.
   * Works for the product page AND quick-view modals.
   *
   * NOTE: We always start from form.parentElement (not form itself) so that
   * a form whose own ID contains "quick" (e.g. id="product-quick-view-form-...")
   * doesn't match itself as the modal container and hide the real ancestors.
   */
  function getHandleFromForm(form) {
    // 0. Halo theme: data-wishlist-handle on the wishlist anchor INSIDE the form
    //    e.g. data-wishlist-handle="my-product-handle"
    const wishlistEl = form.querySelector("[data-wishlist-handle]");
    if (wishlistEl) {
      const h = wishlistEl.dataset.wishlistHandle;
      if (isValidHandle(h)) return h;
    }

    // 1. Walk up PARENT ancestors for data-product-handle / data-handle / data-url
    let node = form.parentElement;
    while (node && node !== document.body) {
      const h =
        node.dataset.productHandle ||
        node.dataset.handle ||
        node.dataset.url?.match(/\/products\/([^/?#]+)/)?.[1] ||
        node.dataset.productUrl?.match(/\/products\/([^/?#]+)/)?.[1];
      if (isValidHandle(h)) return h;
      node = node.parentElement;
    }

    // 2. Find the modal container by walking up from form.parentElement
    //    (using form.parentElement.closest avoids the form matching itself)
    const modalContainer = form.parentElement?.closest(
      '[id*="quick"], [class*="quick"], [id*="modal"], [class*="modal"], [id*="popup"], [class*="popup"]',
    );
    const container = modalContainer || form.parentElement;

    if (container) {
      // 2a. Any element with a product handle data attribute (including wishlist handle)
      const el =
        container.querySelector("[data-product-handle]") ||
        container.querySelector("[data-handle]") ||
        container.querySelector("[data-wishlist-handle]");
      if (el) {
        const h =
          el.dataset.productHandle ||
          el.dataset.handle ||
          el.dataset.wishlistHandle;
        if (isValidHandle(h)) return h;
      }

      // 2b. A link to /products/handle
      for (const link of container.querySelectorAll('a[href*="/products/"]')) {
        const m = (link.getAttribute("href") || "").match(
          /\/products\/([^/?#]+)/,
        );
        if (m && isValidHandle(m[1])) return m[1];
      }

      // 2c. A JSON script block with product data (Section Rendering API)
      for (const script of container.querySelectorAll(
        'script[type="application/json"]',
      )) {
        try {
          const data = JSON.parse(script.textContent);
          const h = data.handle || data.product?.handle;
          if (isValidHandle(h)) return h;
        } catch (_) {}
      }
    }

    // 3. Fall back to URL (product page)
    return getProductHandle();
  }

  /** Get stock for a variant from the given product data. */
  function getStockForVariant(variantId, pd, scopeEl) {
    if (!pd) return DEFAULT_MAX;

    const variant = variantId
      ? pd.variants.find((v) => String(v.id) === String(variantId))
      : pd.variants[0];

    if (!variant) return DEFAULT_MAX;

    if (variant.available === false) return 0;

    // inventory_quantity counts variant UNITS; 1 unit = 0.5 m (STEP), since the
    // cart quantity sent to Shopify is metres / STEP. Available metres is
    // therefore units × STEP — same maximum the Liquid cart dropdowns render.
    const stock = variant.inventory_quantity;
    if (stock && stock > 0) return Math.floor(stock) * STEP;

    // Check product tag: max-50, max-30, max-100, etc.
    if (Array.isArray(pd.tags)) {
      for (const tag of pd.tags) {
        const m = tag.toLowerCase().match(/^max-(\d+(\.\d+)?)$/);
        if (m) {
          const tagMax = parseFloat(m[1]);
          if (tagMax > 0) return tagMax;
        }
      }
    }

    // inventory_quantity is 0 but product IS available
    // Try reading max from Measura's .halo-fabric-dropdown within our scope
    const scope = scopeEl || document;
    const haloSelect = scope.querySelector(".halo-fabric-dropdown");
    if (haloSelect && haloSelect.options.length > 0) {
      const lastOpt = haloSelect.options[haloSelect.options.length - 1];
      const haloMax = parseFloat(lastOpt.value);
      if (!isNaN(haloMax) && haloMax > 0) {
        console.log(
          `[metre-selector] Using halo-fabric-dropdown max: ${haloMax}`,
        );
        return haloMax;
      }
    }

    return DEFAULT_MAX;
  }

  // ── Build options based on stock ───────────────────────────────────────────

  function buildOptions(max) {
    const opts = [];
    const safeMax = Math.max(MIN, max);
    for (
      let v = MIN;
      v <= safeMax + 0.001;
      v = Math.round((v + STEP) * 100) / 100
    ) {
      opts.push(v);
    }
    return opts;
  }

  function refreshDropdown(select, netLenInput, variantId, pd, scopeEl) {
    const max = getStockForVariant(variantId, pd, scopeEl);
    const options = buildOptions(max);
    const current = parseFloat(select.value) || MIN;

    select.innerHTML = "";

    if (max <= 0) {
      const opt = document.createElement("option");
      opt.value = "0";
      opt.textContent = "Out of stock";
      opt.disabled = true;
      select.appendChild(opt);
      netLenInput.value = "0 m";
      return;
    }

    options.forEach((val) => {
      const opt = document.createElement("option");
      opt.value = val.toFixed(1);
      opt.textContent = val.toFixed(1) + " m";
      if (val <= max && Math.abs(val - current) < 0.001) opt.selected = true;
      select.appendChild(opt);
    });

    if (current > max) select.value = options[options.length - 1].toFixed(1);

    netLenInput.value = parseFloat(select.value).toFixed(1) + " m";
  }

  // ── Update Total length + price within the form's container ───────────────

  function updatePageInfo(metres, variantId, pd, scopeEl) {
    const scope = scopeEl || document;

    const totalLenEl = scope.querySelector("[data-halo-total-length]");
    if (totalLenEl) totalLenEl.textContent = metres.toFixed(1) + " m";

    if (!pd) return;

    const variant = variantId
      ? pd.variants.find((v) => String(v.id) === String(variantId))
      : pd.variants[0];
    if (!variant) return;

    const unitPrice = variant.price / 100 / STEP;
    const total = unitPrice * metres;
    const formatted =
      "Rs. " +
      total.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const totalPriceEl = scope.querySelector("[data-halo-total-price]");
    if (totalPriceEl) {
      totalPriceEl.textContent = formatted;
      console.log(
        `[metre-selector] Total length → ${metres.toFixed(1)} m | Price → ${formatted}`,
      );
    }

    const stockEl = scope.querySelector("[data-halo-stock]");
    if (stockEl) {
      const stock = getStockForVariant(variantId, pd, scope);
      stockEl.textContent = stock + " m";
    }
  }

  // ── Inject dropdown into a product form ───────────────────────────────────

  function injectDropdown(form, pd) {
    // mwInjected was set to "pending" by processProductForm; now lock it for real
    form.dataset.mwInjected = "1";

    // Scope container: use parentElement.closest so the form's own id
    // (e.g. "product-quick-view-form-...") doesn't resolve to itself.
    const scopeEl =
      form.parentElement?.closest(
        '[id*="quick"], [class*="quick"], [id*="modal"], [class*="modal"], [id*="popup"], [class*="popup"]',
      ) ||
      form.closest(
        "section, .product-single, .product-info, .product__info-wrapper, .product-form-wrapper, [data-section-type]",
      ) ||
      form.parentElement;

    // 1. Get current variant id
    const variantInput = form.querySelector('[name="id"]');
    const variantId = variantInput?.value;

    // 2. Hide ALL quantity inputs and their wrappers, then inject locked qty=1.
    //
    //    In the Halo quick view the +/- quantity group can live OUTSIDE the <form>
    //    but still inside the modal container (scopeEl). We therefore search both
    //    `form` and `scopeEl` so every instance is covered regardless of placement.
    //
    //    Halo can render two groups:
    //      quantity__group--1  (already hidden by theme JS)
    //      quantity__group--2  (visible, inside .quantity_selector div)
    //    Both are caught by the selectors below.

    const qtySearchRoots = scopeEl !== form ? [form, scopeEl] : [form];

    const qtyInputSelector =
      'input[name="quantity"], .quantity__input, [data-quantity-input]';

    const allQtyInputs = qtySearchRoots.flatMap((root) =>
      Array.from(root.querySelectorAll(qtyInputSelector)),
    );

    // Deduplicate (an input inside form is also inside scopeEl when both roots overlap)
    const seenQty = new Set();
    allQtyInputs.forEach((qi) => {
      if (seenQty.has(qi)) return;
      seenQty.add(qi);

      qi.name = "_mw_qty_disabled";

      // Walk up to the nearest wrapper that represents the whole +/- group.
      const wrapper =
        qi.closest(
          "quantity-quick-view-input, quantity-input, " +
            "[class*='quantity__group'], [class*='quantity-group'], " +
            ".quantity, .product-form__quantity",
        ) ||
        (() => {
          let n = qi.parentElement;
          // Stop at form OR scopeEl — don't walk past the modal boundary
          const boundary = scopeEl || form;
          while (n && n !== boundary) {
            if ((n.className || "").includes("quantity")) return n;
            n = n.parentElement;
          }
          return null;
        })();
      const t = wrapper || qi;
      t.dataset.mwPrehid = "1";
      t.style.setProperty("display", "none", "important");
    });

    // Hide the outer .quantity_selector shell (Halo wraps the group in one).
    // Search both form and scopeEl. Tag with data-mw-prehid so PCS bypass
    // can restore them if the product turns out to be PCS (e.g. on AJAX nav).
    qtySearchRoots.forEach((root) => {
      root
        .querySelectorAll(
          '.quantity_selector, [class*="quantity_selector"], ' +
            "quantity-quick-view-input, quantity-input",
        )
        .forEach((el) => {
          el.dataset.mwPrehid = "1";
          el.style.setProperty("display", "none", "important");
        });
    });

    // Strip name from existing quantity inputs so Fastr (and form submission)
    // only sees our lockedQty — not the theme's original input (value=1).
    form.querySelectorAll('input[name="quantity"]').forEach((inp) => {
      inp.dataset.mwOrigQty = "1";
      inp.removeAttribute("name");
    });

    const lockedQty = document.createElement("input");
    lockedQty.type = "hidden";
    lockedQty.name = "quantity";
    lockedQty.value = String(Math.round(MIN / STEP));
    form.appendChild(lockedQty);

    // 3. Remove existing Net length inputs
    form
      .querySelectorAll(`[name="${NET_LEN_KEY}"], [name="properties[length]"]`)
      .forEach((el) => el.remove());

    // 4. Hide Measura's product dropdown (no-op once Measura is uninstalled)
    const measuraProdSelect = scopeEl.querySelector(".halo-fabric-dropdown");
    if (measuraProdSelect)
      measuraProdSelect.style.setProperty("display", "none", "important");

    // Hide the theme's native subtotal display (.productView-subtotal).
    // It shows price × qty, but for metre products qty is locked to 1 —
    // our "Total price" row in the info block is the correct display instead.
    // PCS products never reach injectDropdown so their subtotal is unaffected.
    scopeEl
      .querySelectorAll(".productView-subtotal")
      .forEach((el) => el.style.setProperty("display", "none", "important"));

    // 5. Hidden Net length input
    const netLenInput = document.createElement("input");
    netLenInput.type = "hidden";
    netLenInput.name = NET_LEN_KEY;
    netLenInput.value = MIN.toFixed(1) + " m";
    form.appendChild(netLenInput);

    // Store refs for fetch interceptor
    formRefs.set(form, { select: null, netLen: netLenInput }); // select filled below

    // 6. Create dropdown
    const select = document.createElement("select");
    select.setAttribute("aria-label", "Select length in metres");
    select.style.cssText = `
      padding: 10px 14px;
      border: 1px solid #ccc;
      border-radius: 0;
      font-size: 15px;
      background: #fff;
      cursor: pointer;
      min-width: 110px;
    `;
    formRefs.get(form).select = select;

    // 7. Dropdown wrapper
    const wrapper = document.createElement("div");
    wrapper.style.cssText =
      "display:flex;align-items:center;gap:12px;margin:14px 0;";
    wrapper.appendChild(select);

    // 8. Info block (Stock / Total length / Total price)
    //    halo-fabric-meter.js used to create this; we now own it.
    //    Reuse an existing one (e.g. left over from Liquid) or build fresh.
    let infoBlock = scopeEl.querySelector(".halo-fabric-info");
    if (!infoBlock) {
      infoBlock = document.createElement("div");
      infoBlock.className = "halo-fabric-info mw-injected";
      infoBlock.style.cssText =
        "margin:6px 0 14px;font-size:13px;line-height:1.7;color:#444;";
      infoBlock.innerHTML =
        '<div class="halo-fabric-info__row">' +
        '<span style="color:#666;margin-right:4px;">Stock:</span>' +
        "<strong data-halo-stock></strong>" +
        "</div>" +
        '<div class="halo-fabric-info__row">' +
        '<span style="color:#666;margin-right:4px;">Total length:</span>' +
        "<strong data-halo-total-length></strong>" +
        "</div>" +
        '<div class="halo-fabric-info__row">' +
        '<span style="color:#666;margin-right:4px;">Total price:</span>' +
        '<strong data-halo-total-price style="font-size:15px;"></strong>' +
        "</div>";
    }

    // 9. Insert dropdown + info block before the Add to Cart button row.
    //    Guard against duplicate injection.
    {
      const submitBtn =
        form.querySelector('[name="add"]') ||
        form.querySelector('button[type="submit"]') ||
        form.querySelector(".btn-addtocart");
      const btnParent = submitBtn
        ? submitBtn.closest(
            ".product-form__buttons, .product-form__submit, .shopify-payment-button",
          ) || submitBtn
        : null;
      const insertParent = btnParent?.parentNode || form;
      const insertBefore = btnParent || null;

      // Guard: don't inject twice
      if (
        insertParent.querySelector(
          'select[aria-label="Select length in metres"]',
        )
      ) {
        console.log(
          "[metre-selector] Insertion point already has dropdown — skipping duplicate",
        );
        return;
      }

      if (insertBefore) {
        insertParent.insertBefore(wrapper, insertBefore);
        insertParent.insertBefore(infoBlock, insertBefore);
      } else {
        insertParent.appendChild(wrapper);
        insertParent.appendChild(infoBlock);
      }
    }

    // 9. Populate dropdown + update info
    refreshDropdown(select, netLenInput, variantId, pd, scopeEl);
    updatePageInfo(parseFloat(select.value) || MIN, variantId, pd, scopeEl);

    // Set initial data-quantity on .shiprocket-headless so Fastr reads the
    // correct qty (metres→units) from its own data attributes.
    const _initQty = String(
      Math.round((parseFloat(select.value) || MIN) / STEP),
    );
    document.querySelectorAll(".shiprocket-headless").forEach((el) => {
      el.setAttribute("data-quantity", _initQty);
      el.setAttribute("data-type", "mini-cart"); // buyCart reads this
    });
    window.__mwCartSynced = false;
    syncBuyNowCart(
      form.querySelector('[name="id"]')?.value || variantId,
      parseFloat(select.value) || MIN,
    );

    // 10. On dropdown change
    select.addEventListener("change", () => {
      const val = parseFloat(select.value).toFixed(1);
      netLenInput.value = val + " m";
      lockedQty.value = String(Math.round(parseFloat(val) / STEP));
      updatePageInfo(parseFloat(val), variantInput?.value, pd, scopeEl);
      console.log(`[metre-selector] Net length → ${val} m`);
      // Update Fastr's data attributes immediately so even a fast click sees the right qty
      const _dq = String(Math.round(parseFloat(val) / STEP));
      document.querySelectorAll(".shiprocket-headless").forEach((el) => {
        el.setAttribute("data-quantity", _dq);
      });
      window.__mwCartSynced = false; // cart not yet synced for this metre value
      syncBuyNowCart(
        form.querySelector('[name="id"]')?.value || variantInput?.value,
        parseFloat(val),
      );
    });

    // 11. On Add-to-Cart submit: clear any pre-synced cart items first so the
    // real /cart/add.js doesn't stack on top of syncBuyNowCart's pre-added qty.
    // The theme uses jQuery Ajax (XHR) which bypasses our fetch interceptor, so
    // we must clean up the cart synchronously before the request fires.
    form.addEventListener(
      "submit",
      async (e) => {
        const metres = parseFloat(select.value) || MIN;
        const qty = Math.round(metres / STEP);
        lockedQty.value = String(qty);
        netLenInput.value = metres.toFixed(1) + " m";

        // ── Stock check: cap quantity to remaining stock before add-to-cart ─────
        // Reads live cart to see how much of this variant is already there.
        // If new add + existing would exceed stock, silently cap to remaining.
        // Only runs when there is already something in cart (alreadyMetres > 0)
        // so a fresh first-add is never interfered with.
        const currentVid =
          form.querySelector('[name="id"]')?.value || variantInput?.value;
        const maxMetres = getStockForVariant(currentVid, pd, scopeEl);
        if (maxMetres > 0 && currentVid) {
          try {
            const _fc = typeof _origFetch === "function" ? _origFetch : fetch;
            const cartData = await _fc.call(window, "/cart.js").then((r) => r.json());
            // Sum metres already in cart for this variant (via Net length property)
            let alreadyMetres = (cartData.items || [])
              .filter(
                (it) =>
                  String(it.variant_id) === String(currentVid) &&
                  it.properties?.["Net length"],
              )
              .reduce(
                (sum, it) => sum + (parseFloat(it.properties["Net length"]) || 0),
                0,
              );
            // syncBuyNowCart pre-adds this variant's CURRENT selection so Buy Now
            // reads a correct cart. That line is removed again further down before
            // the real add fires, so it must not count as already-owned stock —
            // otherwise selecting the max quantity blocks itself as "out of stock".
            if (
              window.__mwCartLine &&
              String(window.__mwCartLine.variant_id) === String(currentVid)
            ) {
              alreadyMetres = Math.max(
                0,
                alreadyMetres - window.__mwCartLine.quantity * STEP,
              );
            }
            // Only intervene when there is already something in cart
            if (alreadyMetres > 0) {
              const remainingMetres = Math.max(0, maxMetres - alreadyMetres);
              if (metres > remainingMetres) {
                if (remainingMetres <= 0) {
                  // Nothing left — block silently (don't add to cart)
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  // Show a small inline notice instead of a blocking alert
                  const notice = document.createElement("div");
                  notice.textContent =
                    `Out of stock — you already have ${alreadyMetres.toFixed(1)} m of this fabric in your cart (max ${maxMetres.toFixed(1)} m).`;
                  notice.style.cssText =
                    "color:#c0392b;font-size:13px;margin:6px 0;";
                  notice.className = "mw-stock-notice";
                  const existingNotice = scopeEl.querySelector(".mw-stock-notice");
                  if (existingNotice) existingNotice.replaceWith(notice);
                  else select.closest(".halo-fabric-dropdown, [class*='quantity'], form") ?.insertAdjacentElement("afterend", notice);
                  setTimeout(() => notice.remove(), 5000);
                  return;
                }
                // Some stock left — silently cap to remaining and continue
                select.value = remainingMetres.toFixed(1);
                lockedQty.value = String(Math.round(remainingMetres / STEP));
                netLenInput.value = remainingMetres.toFixed(1) + " m";
              }
            }
          } catch (_) {
            // Cart fetch failed — allow add, let Shopify enforce limits at checkout
          }
        }

        // If syncBuyNowCart already put this variant in the Shopify cart,
        // remove it now so the Add-to-Cart request starts from 0 units.
        const vid =
          form.querySelector('[name="id"]')?.value || variantInput?.value;
        if (vid && window.__mwCartLine) {
          e.preventDefault();
          e.stopImmediatePropagation();
          try {
            const cart = await _origFetch
              .call(window, "/cart.js")
              .then((r) => r.json());
            const lineIdx = cart.items.findIndex(
              (it) =>
                String(it.variant_id) === String(vid) &&
                it.properties?.["Net length"],
            );
            if (lineIdx >= 0) {
              await _origFetch.call(window, "/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ line: lineIdx + 1, quantity: 0 }),
              });
            }
          } catch (_) {}
          window.__mwCartLine = null;
          window.__mwCartSynced = false;
          // Re-fire the submit now that the cart is clean
          form.dispatchEvent(
            new Event("submit", { bubbles: true, cancelable: true }),
          );
        }
        console.log(
          `[metre-selector] Submit → qty=${qty} | Net length=${netLenInput.value}`,
        );
      },
      true,
    );

    // 12. Watch for variant changes (swatches)
    if (variantInput) {
      const observer = new MutationObserver(() => {
        const newId = variantInput.value;
        refreshDropdown(select, netLenInput, newId, pd, scopeEl);
        updatePageInfo(parseFloat(select.value) || MIN, newId, pd, scopeEl);
      });
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
      variantInput.addEventListener("change", () => {
        refreshDropdown(select, netLenInput, variantInput.value, pd, scopeEl);
        updatePageInfo(
          parseFloat(select.value) || MIN,
          variantInput.value,
          pd,
          scopeEl,
        );
      });
    }

    // 13. Replace "Buy it now" accelerated checkout button.
    //     The native Shopify payment button bypasses the form entirely — it creates
    //     a checkout with qty=1 and NO "Net length" property, so the Cart Transform
    //     cannot apply and the wrong price reaches checkout.
    //     We hide it and inject our own button that adds to cart correctly first.
    const nativePaymentContainer = scopeEl.querySelector(
      '[data-shopify="payment-button"], .shopify-payment-button, .productView-payment',
    );
    if (nativePaymentContainer) {
      nativePaymentContainer.style.setProperty("display", "none", "important");

      const buyNowBtn = document.createElement("button");
      buyNowBtn.type = "button";
      buyNowBtn.className = "mw-buynow";
      buyNowBtn.textContent = "Buy it now";
      // All styling is handled by the .mw-buynow CSS rule injected into <head>
      // (injectBuyNowCSS at top of IIFE). No inline styles needed.

      nativePaymentContainer.insertAdjacentElement("afterend", buyNowBtn);

      // Pin the product form reference directly on the button element.
      // The button is inserted OUTSIDE the <form> (into scopeEl), so btn.closest("form")
      // returns null. metre-checkout-b.js reads _mwForm from the button to get the
      // correct form — the one that has the hidden properties[Net length] input.
      buyNowBtn._mwForm = form;

      // Click handler fires in bubble phase. metre-checkout-b.js event delegation
      // uses capture=true so it fires first and calls stopImmediatePropagation,
      // which would skip this handler. We therefore also register in capture phase
      // so both are at the same phase and the stopImmediatePropagation race doesn't matter.
      // In practice the delegation will fire, read btn._mwForm, and do the right thing.
      buyNowBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Direct fallback in case delegation is blocked for any reason.
        if (typeof window.mwCheckout?.startBuyNow === "function") {
          window.mwCheckout.startBuyNow(form);
        } else {
          console.warn(
            "[metre-selector] mwCheckout not ready, retrying in 500 ms",
          );
          setTimeout(() => {
            if (typeof window.mwCheckout?.startBuyNow === "function") {
              window.mwCheckout.startBuyNow(form);
            } else {
              alert("Checkout is loading, please try again in a moment.");
            }
          }, 500);
        }
      });

      console.log(
        "[metre-selector] Buy it now replaced with Razorpay button ✓",
      );
    }

    console.log(
      `[metre-selector] Injected ✓ max stock: ${getStockForVariant(variantId, pd, scopeEl)}`,
    );
  }

  // ── Process a single product form ─────────────────────────────────────────
  // Detects handle, fetches product data, injects dropdown.
  // Works for both the product page and quick-view modals.

  async function processProductForm(form) {
    if (form.dataset.mwInjected) return;

    // Use parentElement.closest so the form's OWN id (e.g. "product-quick-view-form-...")
    // doesn't incorrectly make it look like a modal form.
    const modalContainer = form.parentElement?.closest(
      '[id*="quick"], [class*="quick"], [id*="modal"], [class*="modal"], [id*="popup"], [class*="popup"]',
    );
    const isModal = !!modalContainer;

    // On the product page (non-modal), only inject into the first form found.
    // This prevents sticky bars / related-product quick-adds from getting a dropdown.
    if (!isModal) {
      if (nonModalFormDone) return;
      nonModalFormDone = true;
    } else {
      // For modal forms, only inject once per modal container.
      // We stamp a data attribute on the container so re-opened modals
      // (same DOM node, different product) still work: the attribute is gone
      // when the theme clears the container's innerHTML.
      if (modalContainer.dataset.mwModalInjected) return;
      modalContainer.dataset.mwModalInjected = "1";
    }

    // Mark IMMEDIATELY (before any await) to prevent the race condition where
    // multiple rapid calls all pass the check before any of them sets the flag.
    form.dataset.mwInjected = "pending";

    // ── Pre-hide SYNCHRONOUSLY (before the async fetchProduct network request) ──
    // The Halo theme's own JS runs during the ~200–500 ms fetch window and can
    // make .halo-fabric-dropdown visible, causing a visible two-step flicker.
    // By hiding qty inputs and the Measura dropdown NOW, the user never sees
    // the intermediate Halo-default UI.
    const earlyScope =
      (isModal ? modalContainer : null) ||
      form.closest(
        "section, .product-single, .product-info, .product__info-wrapper, " +
          ".product-form-wrapper, [data-section-type], [data-product-id]",
      ) ||
      form.parentElement;

    // Hide quantity input wrappers — mark each with data-mw-prehid so the
    // PCS bypass can undo this without the forceShow / restoreIn complexity.
    form
      .querySelectorAll(
        'input[name="quantity"], .quantity__input, [data-quantity-input]',
      )
      .forEach((qi) => {
        const wrapper =
          qi.closest(
            "quantity-quick-view-input, quantity-input, " +
              "[class*='quantity__group'], [class*='quantity-group'], " +
              ".quantity, .product-form__quantity",
          ) || qi.parentElement;
        const target = wrapper || qi;
        target.dataset.mwPrehid = "1";
        target.style.setProperty("display", "none", "important");
      });
    // Hide Measura/Halo fabric dropdown
    earlyScope
      .querySelectorAll(
        ".halo-fabric-dropdown, .halo-measura-select, [class*='fabric-dropdown']",
      )
      .forEach((el) => {
        el.dataset.mwPrehid = "1";
        el.style.setProperty("display", "none", "important");
      });

    // Undo pre-hide (used by error paths and PCS bypass)
    const restorePreHide = (scope) => {
      (scope || earlyScope || form.parentElement || document.body)
        .querySelectorAll("[data-mw-prehid]")
        .forEach((el) => {
          el.style.removeProperty("display");
          delete el.dataset.mwPrehid;
          if (el.name === "_mw_qty_disabled") el.name = "quantity";
        });
    };

    const handle = getHandleFromForm(form);
    if (!handle) {
      console.warn(
        "[metre-selector] Could not find product handle for form:",
        form,
      );
      restorePreHide();
      delete form.dataset.mwInjected;
      if (!isModal) nonModalFormDone = false; // allow retry
      return;
    }

    let pd;
    try {
      pd = await fetchProduct(handle);
    } catch (e) {
      console.warn("[metre-selector] Could not fetch product:", handle, e);
      restorePreHide();
      delete form.dataset.mwInjected;
      return;
    }

    if (!pd) {
      restorePreHide();
      delete form.dataset.mwInjected;
      return;
    }
    // Case-insensitive tag check — Shopify preserves exact capitalisation
    // ("Pcs" ≠ "pcs" in a strict comparison, so we lowercase before checking).
    console.log(`[metre-selector] ${pd.handle} tags:`, pd.tags);
    if (
      Array.isArray(pd.tags) &&
      pd.tags.some((t) => EXCLUDE_TAGS.has(t.toLowerCase()))
    ) {
      console.log(`[metre-selector] pcs product — bypassing, undoing pre-hide`);

      // ── PCS bypass: undo the pre-hide and let the theme run natively ─────────
      restorePreHide();

      // ── Hide only Measura Liquid remnants (not the theme's own qty/form) ─────
      // The Measura app is uninstalled but its Liquid snippet still renders
      // .halo-fabric-dropdown and .halo-fabric-info elements. Hide those only.
      const measuraSelectors = [
        ".halo-fabric-dropdown",
        "[class*='fabric-dropdown']",
        ".halo-fabric-info",
        "[class*='halo-fabric-info']",
        ".halo-fabric-info__row",
        "[data-halo-total-length]",
        "[data-halo-total-price]",
        "[data-halo-stock]",
        ".halo-measura-info",
        // NOTE: .halo-measura-cart-dropdown is our server-rendered cart select —
        // never hide it from here; it lives in the cart, not the product form scope.
      ];
      measuraSelectors.forEach((sel) => {
        undoScope.querySelectorAll(sel).forEach((el) => {
          el.style.setProperty("display", "none", "important");
        });
      });

      form.dataset.mwInjected = "pcs";
      return;
    }

    console.log(
      `[metre-selector] Metre product (${handle}) — injecting dropdown`,
    );

    // If storefront inventory_quantity is 0/missing, fetch real stock from Admin API
    const variantIdForStock =
      form.querySelector('[name="id"]')?.value || pd.variants[0]?.id;
    const storefrontStock = pd.variants?.find(
      (v) => String(v.id) === String(variantIdForStock),
    )?.inventory_quantity;

    if (!storefrontStock || storefrontStock <= 0) {
      const adminStock = await fetchAdminStock(handle, variantIdForStock);
      if (adminStock !== null && adminStock > 0) {
        // Patch the variant so getStockForVariant reads the real value
        pd.variants.forEach((v) => {
          if (String(v.id) === String(variantIdForStock)) {
            v.inventory_quantity = adminStock;
          }
        });
      }
    }

    injectDropdown(form, pd);
  }

  // ── Watch for all product forms (product page + quick views) ─────────────

  function watchForForms() {
    // Reset the per-page flag on SPA navigation
    window.addEventListener("popstate", () => {
      nonModalFormDone = false;
    });

    // Process forms already in the DOM
    document
      .querySelectorAll("form[action*='/cart/add']")
      .forEach(processProductForm);

    // Watch for new forms added dynamically (quick view modals + section re-renders).
    // productCustomerViewing (theme.js) periodically refreshes the product section,
    // replacing the form element entirely. The new form has no data-mw-injected, but
    // nonModalFormDone is still true → we'd skip it and leave the raw Halo HTML visible.
    // Guard: if a new non-modal form appears and NO currently-injected form exists
    // anywhere in the DOM, treat it as a re-render and allow re-injection.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const forms = [];
          if (node.matches?.("form[action*='/cart/add']")) forms.push(node);
          node
            .querySelectorAll?.("form[action*='/cart/add']")
            .forEach((f) => forms.push(f));

          for (const f of forms) {
            const isModal = !!f.parentElement?.closest(
              '[id*="quick"], [class*="quick"], [id*="modal"], [class*="modal"], [id*="popup"], [class*="popup"]',
            );
            if (!isModal && nonModalFormDone) {
              // If no previously-injected form exists in the DOM this is a re-render,
              // not a second form. Reset the flag so the new form gets re-processed.
              const injectedFormExists = !!document.querySelector(
                'form[action*="/cart/add"][data-mw-injected]',
              );
              if (!injectedFormExists) {
                console.log(
                  "[metre-selector] product section re-rendered — resetting nonModalFormDone",
                );
                nonModalFormDone = false;
              }
            }
            processProductForm(f);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Cart helpers ───────────────────────────────────────────────────────────

  function formatRs(amount) {
    return (
      "Rs. " +
      amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function calcDisplayTotal(cartItems) {
    let total = 0;
    for (const item of cartItems) {
      const netLen = item.properties?.["Net length"];
      const metres = netLen ? parseFloat(netLen) : NaN;
      if (!isNaN(metres) && metres > 0) {
        total += item.final_line_price / 100;
      } else {
        total += item.final_line_price / 100;
      }
    }
    return total;
  }

  // Accepts a full /cart.js object (preferred) or a bare items array (legacy).
  // Subtotal and total are written SEPARATELY: the total must come from
  // cart.total_price, which includes cart-level discount codes — an item-sum
  // does not, and writing it over the total shows a wrong figure whenever a
  // discount code is applied.
  function updateCartTotals(cart) {
    if (!cart) return;
    const items = Array.isArray(cart) ? cart : cart.items;
    if (!Array.isArray(items)) return; // error payload from a failed cart call — leave display alone

    const subtotalFmt = formatRs(
      !Array.isArray(cart) && typeof cart.original_total_price === "number"
        ? cart.original_total_price / 100
        : calcDisplayTotal(items),
    );
    const totalFmt =
      !Array.isArray(cart) && typeof cart.total_price === "number"
        ? formatRs(cart.total_price / 100)
        : subtotalFmt;

    // Never set textContent on a container — it wipes labels, shipping rows, etc.
    // Strategy: always dig to the actual price leaf before writing.
    function safeSet(el, fmt) {
      // Already a leaf — write directly
      if (el.children.length === 0) {
        el.textContent = fmt;
        return;
      }
      // Prefer an explicit .money child
      const money = el.querySelector(".money");
      if (money && money.children.length === 0) {
        money.textContent = fmt;
        return;
      }
      // Fallback: find the first leaf whose text looks like a price (digits + decimal)
      const priceLike = Array.from(el.querySelectorAll("*")).find(
        (c) => c.children.length === 0 && /[\d,]+\.\d{2}/.test(c.textContent),
      );
      if (priceLike) priceLike.textContent = fmt;
    }

    document
      .querySelectorAll(
        "[data-cart-subtotal], [data-cart-subtotal-value], " +
          "[data-totals-subtotal], .cart-popup__subtotal-price, " +
          "[class*='cart-subtotal'], [class*='cart__subtotal'], [class*='subtotal__price']",
      )
      .forEach((el) => safeSet(el, subtotalFmt));

    document
      .querySelectorAll(
        "[data-cart-total-value], [data-cart-total], .cart-popup__total-price",
      )
      .forEach((el) => safeSet(el, totalFmt));
  }

  // ── Cart helpers ─────────────────────────────────────────────────────────────

  // Find the item-total price element within a cart row.
  // Tries specific selectors first; falls back to the first leaf .money node.
  function findCartPriceEl(cartRow) {
    // IMPORTANT: querySelector(multiSelector) returns the first element in DOCUMENT
    // ORDER that matches any selector — the order of selectors in the string is
    // irrelevant for priority. To enforce a true priority ordering we call
    // querySelector SEPARATELY for each selector so the first successful call wins.
    const ORDERED = [
      // ── Halo cart-page line-total span (highest priority) ─────────────────
      "[data-item-price-with-quantity-display]", // <span class="cart-item-value">
      ".cart-item-value",
      // ── Shopify data attributes ────────────────────────────────────────────
      "[data-cart-line-price]",
      "[data-item-final-price]",
      // ── Totals-column class patterns ───────────────────────────────────────
      ".cart-item__totals .price-item--regular",
      ".cart-item__totals .price-item",
      ".cart-item__totals .money",
      "[class*='cart-item__total'] .money",
      "[class*='cart-item__total']",
      "[class*='cart-item-total'] .money",
      "[class*='cart-item-total']",
      // ── Sidebar (cart popup) patterns ──────────────────────────────────────
      ".cart-popup-product__final-price .money",
      ".cart-popup-product__final-price",
      ".cart-popup-product__price .money",
      ".cart-popup-product__price",
      // ── Confirmed on Halo sidebar ──────────────────────────────────────────
      ".price-item--regular",
      ".price-item--sale",
      ".price-item",
    ];
    for (const sel of ORDERED) {
      const el = cartRow.querySelector(sel);
      if (el) return el;
    }

    // Fallback 1: first leaf .money element whose text looks like a price
    const leafMoney = Array.from(cartRow.querySelectorAll(".money")).find(
      (el) => el.children.length === 0 && /[\d,]+\.\d{2}/.test(el.textContent),
    );
    if (leafMoney) return leafMoney;

    // Fallback 2: any leaf element with a Rupee / price pattern
    return (
      Array.from(cartRow.querySelectorAll("*")).find(
        (el) =>
          el.children.length === 0 &&
          /(?:Rs\.?|₹)\s*[\d,]+\.?\d*/.test(el.textContent),
      ) || null
    );
  }

  // Update "Net length: X m" text visible in a cart row after the user changes metres.
  // Handles both combined ("Net length: 1.5 m" in one element) and split layouts
  // (<dt>Net length</dt><dd>1.5 m</dd> in adjacent siblings — common in Halo sidebar).
  function updateNetLengthText(cartRow, newMetres) {
    const val = newMetres.toFixed(1) + " m";
    const metreRE = /[\d.]+\s*m\b/;

    // Try to replace a metres value in el or its immediate next sibling / parent's next sibling
    function tryReplace(el) {
      if (!el || el.children.length > 0) return false;
      if (metreRE.test(el.textContent)) {
        el.textContent = el.textContent.replace(metreRE, val);
        return true;
      }
      return false;
    }

    function patchEl(el) {
      if (el.children.length > 0) return;
      const t = el.textContent || "";
      if (!/net\s*length/i.test(t)) return;

      if (metreRE.test(t)) {
        // Combined in one element: "Net length: 1.5 m"
        el.textContent = t.replace(metreRE, val);
        return;
      }

      // Label-only element ("Net length:" with no value in its text).
      // The value may be a TEXT NODE sibling inside the parent — e.g.:
      //   <div><strong>Net length:</strong> 1.5 m</div>
      //   childNodes = [<strong>, TextNode(" 1.5 m")]
      const parent = el.parentElement;
      if (parent) {
        for (const node of parent.childNodes) {
          if (
            node.nodeType === Node.TEXT_NODE &&
            metreRE.test(node.textContent)
          ) {
            node.textContent = node.textContent.replace(metreRE, val);
            return;
          }
        }
      }

      // Fallback: value in a sibling ELEMENT (e.g. <dt>Net length</dt><dd>1.5 m</dd>)
      tryReplace(el.nextElementSibling) ||
        tryReplace(parent && parent.nextElementSibling);
    }

    cartRow.querySelectorAll("*").forEach(patchEl);

    // Directly update the JS-injected net-length span (fastest, no text-walking needed)
    cartRow.querySelectorAll(".mw-net-length-val").forEach((span) => {
      span.textContent = val;
    });
  }

  // Hide "Net length" / "Price per metre" text nodes anywhere in a cart row.
  function hideNetLengthRows(cartRow) {
    const isMetreText = (el) => {
      const t = (el.textContent || "").toLowerCase();
      return t.includes("net length") || t.includes("price per metre");
    };
    cartRow.querySelectorAll("*").forEach((el) => {
      if (el.children.length === 0 && isMetreText(el)) {
        const container = el.closest(
          ".halo-fabric-info__row, [class*='fabric-info'], " +
            ".product-option, [class*='cart-item__property'], [class*='cart__property'], " +
            "[class*='property-item'], [class*='item-property'], " +
            ".cart-item__properties li, li, p",
        );
        const target = container && container !== cartRow ? container : el;
        target.style.setProperty("display", "none", "important");
      }
    });
  }

  // ── Inject metre dropdown into a cart item row ────────────────────────────
  // cartItem.price = base unit price per metre in paise (/cart.js, pre-transform).
  // True line total = (cartItem.price / 100) × metres.

  function injectMetreCartItem(
    cartRow,
    cartItem,
    lineIndex,
    currentMetres,
    pd,
  ) {
    if (cartRow.querySelector(".mw-cart-metre-select")) return;

    if (!currentMetres || isNaN(currentMetres) || currentMetres <= 0)
      currentMetres = MIN;

    const unitPricePerMetre = (cartItem.price || 0) / 100 / STEP;
    const maxStock = getStockForVariant(cartItem.variant_id, pd, null);

    const select = document.createElement("select");
    select.className = "mw-cart-metre-select";
    select.style.cssText =
      "padding:6px 10px;border:1px solid #ccc;border-radius:0;" +
      "font-size:14px;background:#fff;cursor:pointer;min-width:90px;" +
      "display:block;margin:4px 0;";

    for (let m = MIN; ; m = parseFloat((m + STEP).toFixed(1))) {
      const val = parseFloat(m.toFixed(1));
      if (val > maxStock + 0.001) break;
      const opt = document.createElement("option");
      opt.value = String(val);
      opt.textContent = val + " m";
      if (Math.abs(val - currentMetres) < 0.001) opt.selected = true;
      select.appendChild(opt);
    }

    // Insertion point: hide the native qty input and insert our dropdown after it.
    // Fallback chain: quantity-input custom element → qty cell → before price → append.
    const qtyEl = cartRow.querySelector(
      "quantity-input, .cart-item__quantity, " +
        "[class*='quantity-input'], [class*='cart__qty'], [class*='cart-item__qty']",
    );
    if (qtyEl) {
      qtyEl.style.setProperty("display", "none", "important");
      qtyEl.insertAdjacentElement("afterend", select);
    } else {
      const priceRef = findCartPriceEl(cartRow);
      if (priceRef) priceRef.insertAdjacentElement("beforebegin", select);
      else cartRow.appendChild(select);
    }

    // Hide any Measura legacy blocks still present (safe no-op once Liquid is deleted)
    cartRow
      .querySelectorAll(".halo-measura-info, .halo-measura-cart-dropdown")
      .forEach((el) => el.style.setProperty("display", "none", "important"));

    // Fix initial item total display
    const priceEl = findCartPriceEl(cartRow);
    if (priceEl && unitPricePerMetre > 0)
      priceEl.textContent = formatRs(unitPricePerMetre * currentMetres);

    if (!priceEl) {
      // Diagnostic: dump ALL class names in the row + first 600 chars of HTML
      const allClasses = [
        ...new Set(
          Array.from(cartRow.querySelectorAll("*"))
            .map((el) => Array.from(el.classList))
            .flat(),
        ),
      ];
      console.warn(
        "[metre-selector] Metre cart item line " +
          lineIndex +
          " — priceEl NOT found.\n" +
          "ALL classes inside row: " +
          allClasses.join(", ") +
          "\n" +
          "Row HTML (first 800 chars): " +
          cartRow.outerHTML.slice(0, 800),
      );
    } else {
      console.log(
        "[metre-selector] Metre cart item — line " +
          lineIndex +
          ", " +
          currentMetres +
          " m, ₹" +
          unitPricePerMetre +
          "/m, stock: " +
          maxStock +
          " | priceEl: " +
          priceEl.className,
      );
    }

    // On change: update cart + price display + net length label + subtotal
    select.addEventListener("change", async () => {
      const newMetres = parseFloat(select.value);
      select.disabled = true;
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            line: lineIndex,
            quantity: Math.round(newMetres / STEP),
            properties: { "Net length": newMetres.toFixed(1) + " m" },
          }),
        });
        if (!res.ok)
          throw new Error("Cart API " + res.status + ": " + (await res.text()));
        const updatedCart = await res.json();
        const lineTotal = unitPricePerMetre * newMetres;

        // 1. Update item total in this row
        const livePriceEl = findCartPriceEl(cartRow);
        if (livePriceEl) livePriceEl.textContent = formatRs(lineTotal);

        // 2. Update "Net length: X m" text visible in the row
        updateNetLengthText(cartRow, newMetres);

        // 3. Update cart subtotal / total
        updateCartTotals(updatedCart);

        console.log(
          "[metre-selector] ✓ line " +
            lineIndex +
            ": " +
            newMetres +
            " m | " +
            formatRs(lineTotal) +
            " | priceEl found: " +
            !!livePriceEl,
        );
      } catch (err) {
        console.error("[metre-selector] Cart update failed:", err);
        alert("Could not update quantity. Please try again.");
      } finally {
        select.disabled = false;
      }
    });
  }

  // ── Detect cart item rows — zero Measura dependency ─────────────────────
  //
  // Uses only Shopify-standard / plain-HTML anchors:
  //   Cart PAGE  → form[action="/cart"] scopes the search; rows are tbody>tr or ul/ol>li
  //                that contain at least one a[href*="/products/"] link.
  //   Cart SIDEBAR → common Halo / generic container IDs/classes; rows are the
  //                  direct <li> (or first wrapper level) that contain a product link.
  //
  // The DOM order of returned elements matches /cart.js items order (Shopify guarantee).

  function detectCartItemRows() {
    const seen = new WeakSet();
    const rows = [];

    // Match both relative (/products/…) AND absolute (https://…/products/…) links,
    // but exclude known social-share URL patterns.
    const PRODUCT_LINK_SEL =
      'a[href^="/products/"], ' +
      'a[href*="/products/"]:not([href*="sharer"]):not([href*="share"])' +
      ':not([href*="facebook.com"]):not([href*="twitter.com"])' +
      ':not([href*="pinterest"]):not([href*="?u="])' +
      ':not([href*="whatsapp"]):not([href*="mailto:"])';

    function isShareOrNavEl(el) {
      // Reject social-share list items, nav items, footer links, etc.
      const cls = (el.className || "").toLowerCase();
      if (/social|share|footer|breadcrumb|recommend|related/.test(cls))
        return true;
      if (
        el.closest(
          '[class*="social"], [class*="share"], footer, ' +
            '[class*="breadcrumb"], [class*="recommend"], [class*="related"]',
        )
      )
        return true;
      // Reject elements whose ONLY product links are share-style icon buttons (svg, no text)
      const links = Array.from(el.querySelectorAll('a[href^="/products/"]'));
      if (
        links.length &&
        links.every((a) => a.querySelector("svg") && !a.textContent.trim())
      )
        return true;
      return false;
    }

    // Strong signals that an element IS a cart item row
    const QTY_OR_REMOVE_SEL =
      'input[type="number"], input[name="updates[]"], ' +
      '[class*="quantity__input"], [class*="qty-input"], ' +
      'cart-remove-button, [class*="remove-item"], [class*="cart-remove"], ' +
      'a[href*="/cart/change?"], a[href*="quantity=0"]';

    function add(el) {
      if (!el || seen.has(el)) return;
      if (!el.querySelector) return;
      if (!el.querySelector(PRODUCT_LINK_SEL)) return;
      if (isShareOrNavEl(el)) return;
      // Exclude price display sub-blocks (dd/div with price__ class) — not row-level elements
      if (el.closest('[class*="price__"]')) return;
      // Exclude Measura helper elements — they're display-only, not cart rows
      if (el.closest('[class*="measura"], [class*="halo-measura"]')) return;
      seen.add(el);
      rows.push(el);
    }

    // ── 1. Direct class selectors — Halo + common themes ─────────────────────
    document
      .querySelectorAll(
        // Cart PAGE row classes
        ".cart-item, tr.cart-item, li.cart-item, " +
          ".cart__item, tr.cart__item, li.cart__item, " +
          ".cart__row, tr.cart__row, " +
          // Cart SIDEBAR popup item classes (Halo uses these)
          ".cart-popup-product, li.cart-popup-product, " +
          ".cart-popup__product, li.cart-popup__product, " +
          ".cart-popup__item, li.cart-popup__item, " +
          ".header-cart-item, li.header-cart-item, " +
          // Confirmed Halo sidebar class (from live testing)
          ".previewCartItem, li.previewCartItem, " +
          // Data attributes
          "[data-cart-item], [data-cart-line]",
      )
      .forEach(add);

    if (rows.length) return rows;

    // ── 2. Table / list rows inside the cart form (cart PAGE only) ────────────
    const cartForms = Array.from(
      document.querySelectorAll('form[action="/cart"], form[action*="/cart"]'),
    ).filter((f) => f.querySelector(PRODUCT_LINK_SEL));

    for (const form of cartForms) {
      form.querySelectorAll("tbody > tr").forEach(add);
      if (rows.length) break;
      // List items — skip social/share lists
      form.querySelectorAll("ul, ol").forEach((list) => {
        if (isShareOrNavEl(list)) return;
        const cls = (list.className || "").toLowerCase();
        if (/social|share/.test(cls)) return;
        Array.from(list.children).forEach(add);
      });
      if (rows.length) break;
    }

    if (rows.length) return rows;

    // ── 3. Cart SIDEBAR — Halo theme + common patterns ────────────────────────
    const sidebarSelectors = [
      // Halo theme specific
      "#cart-popup",
      ".cart-popup",
      "#CartPopup",
      ".header__cart-popup",
      ".header__cart-popup-inner",
      "[data-cart-popup]",
      "[data-sidebar-cart]",
      // Generic drawer / mini-cart
      "#cart-drawer",
      ".cart-drawer",
      "#CartDrawer",
      "#mini-cart",
      ".mini-cart",
      "#MiniCart",
      "[data-cart-drawer]",
      "[data-mini-cart]",
      // Broad class patterns
      '[class*="cart-popup"]',
      '[class*="cart-drawer"]',
      '[class*="mini-cart"]',
      '[class*="sidebar-cart"]',
      '[class*="cart-panel"]',
      '[class*="cart-flyout"]',
    ];

    for (const sel of sidebarSelectors) {
      let container;
      try {
        container = document.querySelector(sel);
      } catch (_) {
        continue;
      }
      if (!container) continue;
      if (!container.querySelector(PRODUCT_LINK_SEL)) continue; // empty container

      // Prefer a <ul>/<ol> that is not social/nav
      const lists = Array.from(container.querySelectorAll("ul, ol")).filter(
        (l) => !isShareOrNavEl(l),
      );
      for (const list of lists) {
        Array.from(list.children).forEach(add);
        if (rows.length) break;
      }
      if (rows.length) break;

      // Div-based: walk from product links upward until siblings point to DIFFERENT products
      if (!rows.length) {
        container.querySelectorAll(PRODUCT_LINK_SEL).forEach((link) => {
          let node = link.parentElement;
          while (node && node !== container) {
            const parent = node.parentElement;
            if (!parent) break;
            const childLinks = Array.from(parent.children)
              .map((s) => s.querySelector?.(PRODUCT_LINK_SEL))
              .filter(Boolean);
            const uniqueHrefs = new Set(
              childLinks.map(
                (a) => a.getAttribute("href")?.split("?")[0] || "",
              ),
            );
            if (uniqueHrefs.size > 1) {
              Array.from(parent.children).forEach(add);
              break;
            }
            if (parent === container) {
              add(node);
              break;
            }
            node = parent;
          }
        });
        if (rows.length) break;
      }
    }

    // ── 4. Last-resort: product link + qty/remove button (strong cart-item signal) ─
    // Price blocks and Measura info divs never have quantity inputs or remove buttons.
    // Real cart item rows always do.
    if (!rows.length) {
      const allProductLinks = Array.from(
        document.querySelectorAll(PRODUCT_LINK_SEL),
      ).filter((a) => !isShareOrNavEl(a));
      for (const link of allProductLinks) {
        let node = link.parentElement;
        while (node && node.tagName !== "BODY") {
          if (
            node.querySelector(PRODUCT_LINK_SEL) &&
            node.querySelector(QTY_OR_REMOVE_SEL) &&
            node.querySelectorAll(PRODUCT_LINK_SEL).length <= 4
          ) {
            add(node);
            break;
          }
          node = node.parentElement;
        }
      }
    }

    if (!rows.length) {
      // Diagnostic dump to help identify the correct selectors
      const productLinks = Array.from(
        document.querySelectorAll(PRODUCT_LINK_SEL),
      ).filter((a) => !isShareOrNavEl(a));
      const linkInfo = productLinks.slice(0, 5).map((a) => {
        const parent = a.parentElement;
        const gp = parent?.parentElement;
        return (
          "<" +
          (parent?.tagName || "?").toLowerCase() +
          ' class="' +
          (parent?.className || "") +
          '"> → ' +
          "<" +
          (gp?.tagName || "?").toLowerCase() +
          ' class="' +
          (gp?.className || "") +
          '">'
        );
      });
      console.warn(
        "[metre-selector] Could not detect cart item rows.\n" +
          "Product links found on page (" +
          productLinks.length +
          " total, showing up to 5):\n" +
          linkInfo.join("\n") +
          "\n" +
          "If cart sidebar is open, right-click a cart item → Inspect → note tag+class of the row.",
      );
    }

    return rows;
  }

  // ── Watch for cart items (cart page + cart drawer) ────────────────────────

  async function initCartDropdowns() {
    // ── FAST PATH: hook into Liquid-rendered Measura selects ─────────────────
    // The Halo theme's main-cart.liquid already renders a <select> per line with:
    //   data-line-index  = 1-based line number  (for /cart/change.js)
    //   data-unit-price  = unit price in paise   (item.price from Shopify)
    //   data-property-name = "Net length"
    //   value            = current metres        (pre-selected by Liquid)
    // Measura's JS was deleted; we just need to re-attach a change handler.
    const unhooked = Array.from(
      document.querySelectorAll(
        ".halo-measura-cart-dropdown:not([data-mw-hooked])",
      ),
    );

    if (unhooked.length) {
      // Group all unhooked selects by data-line-index.
      // Liquid renders TWO copies per line (mobile + desktop); we must attach the
      // change handler to BOTH so the user's interaction is caught regardless of
      // which one is visible.
      const byLine = new Map(); // lineIdxStr → select[]
      unhooked.forEach((sel) => {
        const idx = sel.dataset.lineIndex;
        if (!idx) return;
        sel.dataset.mwHooked = "true"; // mark ALL copies so the slow-path guard sees them
        if (!byLine.has(idx)) byLine.set(idx, []);
        byLine.get(idx).push(sel);
      });

      // Fetch live cart for totals — use _origFetch to bypass the Buy Now fake-cart
      // interceptor, which returns a single-item fake cart on product pages and would
      // cause metre items to be misidentified as PCS (storedMetres = 0).
      let cartObj = null;
      let cartLines = [];
      try {
        const _fetchCart = typeof _origFetch === "function" ? _origFetch : fetch;
        const r = await _fetchCart.call(window, "/cart.js");
        if (r.ok) {
          cartObj = await r.json();
          cartLines = cartObj.items || [];
        }
      } catch (_) {}

      updateCartTotals(cartObj || cartLines);

      byLine.forEach((selects, lineIdxStr) => {
        const primary = selects[0]; // read data-attributes from the first copy
        const lineIndex = parseInt(lineIdxStr, 10) || 1;
        const unitPricePerMetre =
          (parseInt(primary.dataset.unitPrice, 10) || 0) / 100 / STEP;
        const propertyName = primary.dataset.propertyName || "Net length";

        // Get the real stored metres from live cart data (more reliable than Liquid-rendered
        // select value, which falls back to item.quantity when measura_length_number is nil)
        const cartItem = cartLines[lineIndex - 1];
        const storedNetLen = cartItem?.properties?.[propertyName] || "";
        const storedMetres = parseFloat(storedNetLen) || 0;

        // ── PCS bypass: no Net length property → not a metre item ────────────
        // Hide the Measura select and inject a simple [- N +] qty widget so the
        // customer can still change quantity. Metre logic is skipped entirely.
        if (!storedMetres) {
          const currentQty = cartItem?.quantity || 1;
          const unitPricePaise = cartItem?.price || 0; // paise per unit
          let pcsBusy = false;

          // Find the OUTERMOST cart item row from the primary select.
          // Must be the full .cart-item div (not .cart-item-wrapper) so that
          // both the mobile AND desktop qty columns are inside it.
          const pcsCartRow = (() => {
            let node = primary.parentElement;
            let bestRow = null;
            for (let i = 0; i < 16 && node && node.tagName !== "BODY"; i++) {
              if (
                node.matches?.(
                  ".cart-item, [data-item-row], .previewCartItem, .cart-popup-product",
                )
              ) {
                bestRow = node; // keep walking — want the outermost match
              }
              node = node.parentElement;
            }
            if (bestRow) return bestRow;
            // fallback: first ancestor with a product link
            node = primary.parentElement;
            for (let i = 0; i < 14 && node && node.tagName !== "BODY"; i++) {
              if (node.querySelector?.('a[href^="/products/"]')) return node;
              node = node.parentElement;
            }
            return primary.parentElement;
          })();

          // Collect all cart-update-quantity wrappers for this line so we can
          // sync ALL qty widget inputs after an update (mobile + desktop copies).
          const pcsWrappers = [];

          selects.forEach((sel) => {
            const wrapper =
              sel.closest("cart-update-quantity") || sel.parentElement;
            // Hide the entire cart-update-quantity wrapper (not just the select)
            // so the theme's custom-element JS cannot interfere with our widget.
            wrapper.style.setProperty("display", "none", "important");
            pcsWrappers.push(wrapper);

            if (wrapper.querySelector(".mw-pcs-cart-qty")) return; // already done

            const qtyWrap = document.createElement("div");
            qtyWrap.className = "mw-pcs-cart-qty";
            qtyWrap.style.cssText = "display:flex;align-items:center;gap:4px;";
            qtyWrap.innerHTML =
              `<button type="button" class="quantity__button no-js-hidden" name="minus" style="width:28px;height:28px;border:1px solid #ccc;background:#fff;cursor:pointer;font-size:16px;">−</button>` +
              `<input type="number" class="quantity__input" value="${currentQty}" min="1" style="width:40px;text-align:center;border:1px solid #ccc;padding:4px;font-size:14px;">` +
              `<button type="button" class="quantity__button no-js-hidden" name="plus" style="width:28px;height:28px;border:1px solid #ccc;background:#fff;cursor:pointer;font-size:16px;">+</button>`;

            wrapper.insertAdjacentElement("afterend", qtyWrap);

            const numInput = qtyWrap.querySelector("input");
            const updateQty = async (newQty) => {
              if (pcsBusy || newQty < 1) return;
              pcsBusy = true;
              qtyWrap
                .querySelectorAll("button")
                .forEach((b) => (b.disabled = true));
              try {
                const res = await fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ line: lineIndex, quantity: newQty }),
                });
                const updatedCart = await res.json();

                // Sync all qty widget inputs for this line (mobile + desktop)
                pcsWrappers.forEach((w) => {
                  w.querySelectorAll(
                    ".mw-pcs-cart-qty input, .mw-pcs-cart-qty + * input",
                  ).forEach((inp) => {
                    inp.value = newQty;
                  });
                });
                // Also walk siblings: our widget is inserted after each wrapper
                pcsWrappers.forEach((w) => {
                  const sib = w.nextElementSibling;
                  if (sib?.classList.contains("mw-pcs-cart-qty")) {
                    const inp = sib.querySelector("input");
                    if (inp) inp.value = newQty;
                  }
                });

                // Update the line item total shown in the cart row
                const lineTotal = (unitPricePaise / 100) * newQty;
                const priceEl = findCartPriceEl(pcsCartRow);
                if (priceEl) priceEl.textContent = formatRs(lineTotal);

                // Update "Qty: N" text label in sidebar's halo-measura-info block
                pcsCartRow
                  .querySelectorAll(".halo-measura-info strong")
                  .forEach((strong) => {
                    if (/^qty[:\s]/i.test(strong.textContent.trim())) {
                      const parent = strong.parentElement;
                      for (const node of parent.childNodes) {
                        if (node.nodeType === Node.TEXT_NODE) {
                          node.textContent = " " + newQty;
                          break;
                        }
                      }
                    }
                  });

                updateCartTotals(updatedCart);
              } catch (e) {
                console.error(
                  "[metre-selector] PCS cart qty update failed:",
                  e,
                );
              } finally {
                qtyWrap
                  .querySelectorAll("button")
                  .forEach((b) => (b.disabled = false));
                pcsBusy = false;
              }
            };
            qtyWrap
              .querySelector("[name='minus']")
              .addEventListener("click", () => {
                const v = Math.max(1, parseInt(numInput.value, 10) - 1);
                numInput.value = v;
                updateQty(v);
              });
            qtyWrap
              .querySelector("[name='plus']")
              .addEventListener("click", () => {
                const v = parseInt(numInput.value, 10) + 1;
                numInput.value = v;
                updateQty(v);
              });
            numInput.addEventListener("change", () =>
              updateQty(Math.max(1, parseInt(numInput.value, 10) || 1)),
            );
          });

          return; // skip metre logic for this line
        }

        // Sync all select copies to the stored value so the UI is accurate on page load.
        // If storedMetres exceeds the highest available option (stock ran out), the
        // native <select> shows blank. In that case inject a warning option so the
        // customer can see the over-stock quantity and choose to reduce it.
        if (storedMetres > 0)
          selects.forEach((s) => {
            s.value = storedMetres.toFixed(1);
            if (parseFloat(s.value) !== storedMetres) {
              // No matching option — cart quantity exceeds current stock.
              const availableOpts = Array.from(s.options).filter((o) => !o.disabled && parseFloat(o.value) > 0);
              const maxAvail = availableOpts.length ? parseFloat(availableOpts[availableOpts.length - 1].value) : 0;
              const warningOpt = document.createElement("option");
              warningOpt.value = storedMetres.toFixed(1);
              warningOpt.textContent =
                storedMetres.toFixed(1) +
                " m ⚠ (max " +
                (maxAvail > 0 ? maxAvail.toFixed(1) + " m" : "out of stock") +
                ")";
              warningOpt.style.color = "#c0392b";
              s.insertBefore(warningOpt, s.firstChild);
              s.value = storedMetres.toFixed(1);
            }
          });

        // Walk up to the actual cart-item row.
        // We need the element that contains BOTH a product link AND a price element —
        // that is the full cart-item wrapper (not just the inner info sub-div).
        const PRICE_SEL =
          '[class*="price"], .money, [data-price], [data-item-price],' +
          "[data-item-price-with-quantity-display]," +
          '[class*="cart-item__total"], [class*="cart-popup-product__price"]';
        const cartRow = (() => {
          // 1. Known class names — but skip quantity-control elements (cart-update-quantity)
          const byClass = primary.closest(
            ".cart-item, [data-item-row], " +
              ".cart-popup-product, .cart-popup__product, .cart-popup__item, " +
              '[class*="mini-cart__item"], [class*="sidebar-cart__item"]',
          );
          // Reject if it matched a qty wrapper (not a real cart-item row)
          if (
            byClass &&
            byClass.tagName.toLowerCase() !== "cart-update-quantity"
          )
            return byClass;

          // 2. Walk up: find the first ancestor that contains BOTH a product link AND a price
          let node = primary.parentElement;
          for (let i = 0; i < 14 && node && node.tagName !== "BODY"; i++) {
            if (
              node.querySelector('a[href^="/products/"]') &&
              node.querySelector(PRICE_SEL)
            )
              return node;
            node = node.parentElement;
          }
          // 3. Fallback: first ancestor with any product link
          node = primary.parentElement;
          for (let i = 0; i < 10 && node && node.tagName !== "BODY"; i++) {
            if (node.querySelector('a[href^="/products/"]')) return node;
            node = node.parentElement;
          }
          return primary.parentElement;
        })();
        console.log(
          "[metre-selector] line " + lineIdxStr + " cartRow →",
          cartRow.tagName,
          cartRow.className.slice(0, 80),
          "| copies:",
          selects.length,
        );

        // Fix initial total (Shopify shows qty×unit_price; we want metres×unit_price)
        const metres0 =
          storedMetres > 0 ? storedMetres : parseFloat(primary.value) || 1;
        const priceEl0 = findCartPriceEl(cartRow);
        if (priceEl0 && unitPricePerMetre > 0)
          priceEl0.textContent = formatRs(unitPricePerMetre * metres0);

        // ── Inject info block if Liquid didn't render one ─────────────────────
        // halo-measura-info is guarded by {%- if measura_length_number -%} in Liquid.
        // After Measura app uninstall that variable is nil, so the block is never
        // rendered. We create it from JS using the data we already have.
        if (!cartRow.querySelector(".halo-measura-info")) {
          const infoDiv = document.createElement("div");
          infoDiv.className = "halo-measura-info mw-js-info";
          infoDiv.style.cssText =
            "margin:6px 0 10px;font-size:13px;line-height:1.7;color:#444;";
          infoDiv.innerHTML =
            '<div><strong>Net length:</strong> <span class="mw-net-length-val">' +
            metres0.toFixed(1) +
            " m" +
            "</span></div>" +
            "<div><strong>Unit price:</strong> " +
            formatRs(unitPricePerMetre) +
            "/m</div>";
          // Insert the info block in a column that is visible on desktop.
          // The DOM may have NESTED hidden columns (.small-screen-visible inside
          // another .small-screen-visible). We must climb to the OUTERMOST hidden
          // ancestor so the info block lands outside all of them.
          const qtyWrapper =
            primary.closest("cart-update-quantity") || primary.parentElement;
          const HIDDEN_RE =
            /small.?screen.?visible|mobile.?only|hidden.?desk|hide.?desk/i;
          let anchor = qtyWrapper;
          let node = qtyWrapper.parentElement;
          // Walk up to (but not past) cartRow, keeping track of the last hidden ancestor
          while (node && node !== cartRow && node.tagName !== "BODY") {
            if (HIDDEN_RE.test(String(node.className || ""))) anchor = node;
            node = node.parentElement;
          }
          // If no hidden ancestor found, fall back to the qty wrapper itself
          anchor.insertAdjacentElement("afterend", infoDiv);
          console.log(
            "[metre-selector] injected info block after",
            anchor.tagName + "." + String(anchor.className).slice(0, 80),
            "| parent:",
            anchor.parentElement?.tagName +
              "." +
              String(anchor.parentElement?.className || "").slice(0, 60),
            "| metres:",
            metres0,
            "| price:",
            unitPricePerMetre,
          );
        } else {
          console.log(
            "[metre-selector] halo-measura-info already exists in cartRow — skipping inject",
          );
        }

        // Use a shared busy-flag so mobile + desktop copies don't trigger two API calls
        let busy = false;

        // Attach the change handler to EVERY copy (mobile + desktop)
        selects.forEach((select) => {
          select.addEventListener("change", async () => {
            if (busy) return;
            busy = true;

            const newMetres = parseFloat(select.value);
            // Sync sibling copies to match the one the user changed
            selects.forEach((s) => {
              if (s !== select) s.value = select.value;
            });
            selects.forEach((s) => {
              s.disabled = true;
            });

            try {
              const res = await fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  line: lineIndex,
                  quantity: Math.round(newMetres / STEP),
                  properties: { [propertyName]: newMetres.toFixed(1) + " m" },
                }),
              });
              if (!res.ok)
                throw new Error(
                  "Cart API " + res.status + ": " + (await res.text()),
                );
              const updatedCart = await res.json();

              const lineTotal = unitPricePerMetre * newMetres;
              const livePriceEl = findCartPriceEl(cartRow);
              if (livePriceEl) livePriceEl.textContent = formatRs(lineTotal);

              updateNetLengthText(cartRow, newMetres);
              updateCartTotals(updatedCart);

              console.log(
                "[metre-selector] ✓ line " +
                  lineIndex +
                  ": " +
                  newMetres +
                  " m | " +
                  formatRs(lineTotal) +
                  " | priceEl: " +
                  (livePriceEl
                    ? livePriceEl.className || livePriceEl.tagName
                    : "NOT FOUND") +
                  " | cartRow: " +
                  cartRow.tagName +
                  "." +
                  cartRow.className.slice(0, 60),
              );
            } catch (err) {
              console.error("[metre-selector] Cart update failed:", err);
              alert("Could not update quantity. Please try again.");
            } finally {
              selects.forEach((s) => {
                s.disabled = false;
              });
              busy = false;
            }
          });
        });

        console.log(
          "[metre-selector] Hooked cart line " +
            lineIndex +
            " | " +
            metres0 +
            " m | ₹" +
            unitPricePerMetre +
            "/m" +
            " | selects: " +
            selects.length,
        );
      });

      return; // fast path done — no injection needed
    }

    // ── SLOW PATH: inject our own selects (sidebar or after Measura Liquid removed) ─
    // GUARD: if ANY Measura-rendered select exists on this page (hooked or freshly
    // rendered after a cart re-render), skip the slow path entirely. The slow path's
    // _processCartLine hides .halo-measura-info and .halo-measura-cart-dropdown,
    // which breaks the fast-path UI. On pages with Measura selects, the fast path
    // is solely responsible for wiring up dropdowns.
    if (document.querySelector(".halo-measura-cart-dropdown")) return;

    // Quick bail-out: if no cart-specific containers exist in the DOM this is
    // a product/collection page (product cards are NOT cart rows) — skip silently.
    if (
      !document.querySelector(
        'form[action="/cart"], .cart__items, .cart-item, ' +
          '[class*="cart-items"], [data-cart-items], #CartContainer, .cart-drawer',
      )
    )
      return;

    const allRows = detectCartItemRows();
    const cartRows = allRows.filter((r) => !r.dataset.mwCartLine);
    if (!cartRows.length) return;

    cartRows.forEach((r) => {
      r.dataset.mwCartLine = "processing";
    });

    let cartObj = null;
    let cartLines = [];
    try {
      const _fetchCart = typeof _origFetch === "function" ? _origFetch : fetch;
      const res = await _fetchCart.call(window, "/cart.js");
      if (res.ok) {
        cartObj = await res.json();
        cartLines = cartObj.items || [];
      }
    } catch (_) {}

    if (!cartLines.length) {
      cartRows.forEach((r) => {
        delete r.dataset.mwCartLine;
      });
      return;
    }

    updateCartTotals(cartObj);

    const productDataMap = {};
    await Promise.all(
      [...new Set(cartLines.map((it) => it.handle).filter(Boolean))].map(
        async (h) => {
          try {
            productDataMap[h] = await fetchProduct(h);
          } catch (_) {}
        },
      ),
    );

    cartLines.forEach((cartItem, i) => {
      const cartRow = cartRows[i];
      if (!cartRow) return;
      cartRow.dataset.mwCartLine = String(i + 1);
      _processCartLine(cartRow, cartItem, i + 1, productDataMap);
    });
  }

  function _processCartLine(cartRow, cartItem, lineIndex, productDataMap) {
    const netLenProp = cartItem.properties?.["Net length"];
    const pd = productDataMap[cartItem.handle];
    const isPcsProduct = Array.isArray(pd?.tags)
      ? pd.tags.some((t) => EXCLUDE_TAGS.has(t.toLowerCase()))
      : false;

    if (isPcsProduct || !netLenProp) {
      // ── pcs item ───────────────────────────────────────────────────────────
      const unitPrice = (cartItem.price || 0) / 100;
      const qty = cartItem.quantity || 1;

      const priceEl = findCartPriceEl(cartRow);
      if (priceEl && unitPrice > 0)
        priceEl.textContent = formatRs(unitPrice * qty);

      hideNetLengthRows(cartRow);

      cartRow
        .querySelectorAll(".halo-measura-cart-dropdown, .halo-fabric-dropdown")
        .forEach((el) => el.style.setProperty("display", "none", "important"));

      if (netLenProp) {
        cartRow.querySelectorAll(".halo-measura-info *").forEach((el) => {
          const t = (el.textContent || "").toLowerCase();
          if (t.includes("net length") || t.includes("price per metre"))
            el.style.setProperty("display", "none", "important");
        });
      }

      const notInMeasura = (el) =>
        !el.closest(
          ".halo-measura-info, .halo-measura-cart-dropdown, .halo-fabric-dropdown",
        );

      cartRow
        .querySelectorAll(
          "quantity-input, quantity-quick-view-input, " +
            "[class*='quantity-input'], [class*='quantity__group'], " +
            ".cart-item__quantity, [class*='cart-item__qty'], [class*='cart__qty'], .qty-selector",
        )
        .forEach((el) => {
          if (notInMeasura(el)) forceShow(el, cartRow);
        });

      cartRow
        .querySelectorAll(
          'input[name^="updates"], input[data-quantity-input], input[data-cart-quantity], ' +
            '.cart-item__qty-input, input[class*="quantity__input"], ' +
            '[class*="quantity__input"] input, quantity-input input, .qty input, input[type="number"]',
        )
        .forEach((el) => {
          if (notInMeasura(el)) forceShow(el, cartRow);
        });

      const foundInputs = cartRow.querySelectorAll(
        'input[name^="updates"], quantity-input input, input[type="number"]',
      ).length;
      console.log(
        "[metre-selector] pcs cart line " +
          lineIndex +
          " — inputs: " +
          foundInputs,
      );

      if (foundInputs === 0 && !cartRow.querySelector(".mw-pcs-qty")) {
        const currentQty = cartItem.quantity || 1;
        const qtyWrap = document.createElement("div");
        qtyWrap.className = "mw-pcs-qty";
        qtyWrap.innerHTML =
          '<button type="button" data-mw-minus>−</button>' +
          '<input type="number" data-mw-qty value="' +
          currentQty +
          '" min="1">' +
          '<button type="button" data-mw-plus>+</button>';

        const legacySelect = cartRow.querySelector(
          ".halo-measura-cart-dropdown",
        );
        const qtyCell = cartRow.querySelector(
          ".cart-item__quantity, [class*='cart__qty'], [class*='cart-item__qty']",
        );
        if (legacySelect) {
          legacySelect.parentNode.insertBefore(qtyWrap, legacySelect);
        } else if (qtyCell) {
          qtyCell.appendChild(qtyWrap);
        } else {
          cartRow.appendChild(qtyWrap);
        }

        const qtyInput = qtyWrap.querySelector("[data-mw-qty]");

        async function updatePcsQty(newQty) {
          if (newQty < 1) return;
          qtyInput.value = newQty;
          qtyWrap.style.opacity = "0.5";
          qtyWrap.style.pointerEvents = "none";
          try {
            const r = await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                line: lineIndex,
                quantity: newQty,
                properties: { "Net length": "" },
              }),
            });
            if (!r.ok) throw new Error(await r.text());
            const updatedCart = await r.json();
            const lineUnitPrice = (cartItem.price || 0) / 100;
            const livePriceEl = findCartPriceEl(cartRow);
            if (livePriceEl && lineUnitPrice > 0)
              livePriceEl.textContent = formatRs(lineUnitPrice * newQty);
            updateCartTotals(updatedCart);
            console.log(
              "[metre-selector] pcs line " + lineIndex + " → qty " + newQty,
            );
          } catch (err) {
            console.error("[metre-selector] pcs qty update failed:", err);
            qtyInput.value = currentQty;
          } finally {
            qtyWrap.style.opacity = "";
            qtyWrap.style.pointerEvents = "";
          }
        }

        qtyWrap
          .querySelector("[data-mw-minus]")
          .addEventListener("click", () =>
            updatePcsQty(parseInt(qtyInput.value, 10) - 1),
          );
        qtyWrap
          .querySelector("[data-mw-plus]")
          .addEventListener("click", () =>
            updatePcsQty(parseInt(qtyInput.value, 10) + 1),
          );
        qtyInput.addEventListener("change", () => {
          const v = parseInt(qtyInput.value, 10);
          if (!isNaN(v) && v >= 1) updatePcsQty(v);
        });

        if (netLenProp) {
          fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              line: lineIndex,
              quantity: currentQty,
              properties: { "Net length": "" },
            }),
          }).catch(() => {});
        }
      }
      console.log("[metre-selector] pcs cart line " + lineIndex + " processed");
      return;
    }

    // ── metre item ────────────────────────────────────────────────────────────
    const metres = parseFloat(netLenProp);
    injectMetreCartItem(
      cartRow,
      cartItem,
      lineIndex,
      isNaN(metres) ? MIN : metres,
      pd,
    );
  }

  // Watch for dynamically loaded cart drawers.
  // Debounced — each DOM mutation resets the 300 ms timer so rapid successive
  // mutations (e.g. cart drawer opening, our own widget injection) result in
  // only ONE call to initCartDropdowns instead of dozens.
  let _cartInitTimer = null;
  const cartObserver = new MutationObserver(() => {
    clearTimeout(_cartInitTimer);
    _cartInitTimer = setTimeout(initCartDropdowns, 300);
  });
  cartObserver.observe(document.body, { childList: true, subtree: true });

  // ── Cleanup: remove stale "Net length" from PCS-tagged cart items ────────────
  // PCS products added before the PCS bypass was deployed may have a "Net length"
  // property from when they were incorrectly treated as metre items. This removes
  // it so it doesn't appear on the Shopify checkout order summary.

  async function cleanupPcsNetLength() {
    try {
      const _fetchCart = typeof _origFetch === "function" ? _origFetch : fetch;
      const r = await _fetchCart.call(window, "/cart.js");
      if (!r.ok) return;
      const cart = await r.json();
      const items = cart.items || [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.properties?.["Net length"]) continue; // nothing to clear
        const handle = item.handle;
        if (!handle) continue;
        try {
          const pd = await fetchProduct(handle);
          if (
            !pd ||
            !Array.isArray(pd.tags) ||
            !pd.tags.some((t) => EXCLUDE_TAGS.has(t.toLowerCase()))
          )
            continue;
          // PCS product with stale "Net length" property — clear it
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              line: i + 1,
              quantity: item.quantity,
              properties: { "Net length": "" },
            }),
          });
          console.log(
            "[metre-selector] Cleared stale Net length from PCS cart line " +
              (i + 1),
          );
        } catch (_) {}
      }
    } catch (_) {}
  }

  // ── Main ───────────────────────────────────────────────────────────────────

  async function init() {
    // Cart dropdowns on all pages
    initCartDropdowns();

    // Product forms — product page + quick views
    watchForForms();

    // Remove stale "Net length" properties from PCS products in the cart
    cleanupPcsNetLength();
  }

  // ── Intercept AJAX /cart/add.js to force quantity=1 + Net length ────────────
  // Find the active/visible injected form's select to get the right value.

  // Fastr/Shiprocket checkout is only present when its script has injected the
  // .shiprocket-headless element (or exposed its events object). The fake
  // /cart.js and /cart/add responses below exist ONLY to feed Fastr's overlay.
  // Razorpay Magic Checkout reads the REAL cart — faking responses for it makes
  // order creation fail and drops the customer to native Shopify checkout.
  function _fastrActive() {
    return (
      !!document.querySelector(".shiprocket-headless") ||
      typeof window.shiprocketCheckoutEvents !== "undefined"
    );
  }

  const _origFetch = window.fetch;
  window.fetch = async function (input, init, ...rest) {
    const url = typeof input === "string" ? input : input?.url || "";

    // Intercept ALL external POST calls from Fastr (pickrr / fastrr / shiprocket domains)
    const isFastrDomain =
      url.includes("pickrr.com") ||
      url.includes("fastrr") ||
      url.includes("shiprocket.in");

    if (isFastrDomain && init?.method?.toUpperCase() === "POST") {
      // Analytics: return fake 200 so checkout is not blocked by 400
      if (url.includes("/collect")) {
        console.log("[metre-selector] Pickrr analytics — returning fake 200");
        _origFetch.call(this, input, init, ...rest).catch(() => {});
        return Promise.resolve(
          new Response("{}", {
            status: 200,
            headers: { "Content-Type": "application/json" },
          }),
        );
      }

      // Checkout creation / other APIs: log body and try to inject correct qty
      const qty = window.__mwBuyNowQty;
      if (qty && typeof init?.body === "string") {
        try {
          const body = JSON.parse(init.body);
          console.log(
            "[metre-selector] Fastr API →",
            url.replace(/^https?:\/\//, "").split("?")[0],
            JSON.stringify(body).slice(0, 400),
          );

          let changed = false;
          if (body.quantity !== undefined) {
            body.quantity = qty;
            changed = true;
          }
          if (Array.isArray(body.items)) {
            body.items = body.items.map((i) => ({ ...i, quantity: qty }));
            changed = true;
          }
          if (Array.isArray(body.products)) {
            body.products = body.products.map((p) => ({ ...p, quantity: qty }));
            changed = true;
          }
          if (Array.isArray(body.lineItems)) {
            body.lineItems = body.lineItems.map((l) => ({
              ...l,
              quantity: qty,
            }));
            changed = true;
          }
          if (Array.isArray(body.line_items)) {
            body.line_items = body.line_items.map((l) => ({
              ...l,
              quantity: qty,
            }));
            changed = true;
          }

          if (changed) {
            console.log(
              "[metre-selector] Fastr API — qty patched to",
              qty,
              "→",
              JSON.stringify(body).slice(0, 400),
            );
            init = { ...init, body: JSON.stringify(body) };
          } else {
            console.log(
              "[metre-selector] Fastr API — no qty field found to patch",
            );
          }
        } catch (_) {}
      } else if (!qty) {
        // Log even if we don't have a qty to patch — helps us see the payload
        if (typeof init?.body === "string") {
          console.log(
            "[metre-selector] Fastr API (no qty captured) →",
            url.replace(/^https?:\/\//, "").split("?")[0],
            init.body.slice(0, 400),
          );
        }
      }
    }

    // ── Fastr's cart-mode /cart.js read — return fake cart with correct qty/price ──
    // In cart-mode Fastr reads /cart.js to populate its checkout overlay.
    // syncBuyNowCart uses _origFetch, so every /cart.js that reaches this interceptor
    // comes from Fastr (or the theme). We fake it so the overlay shows the right values.
    if (
      url.endsWith("/cart.js") &&
      (!init?.method || init.method.toUpperCase() === "GET") &&
      window.location.pathname.includes("/products/") &&
      window.__mwCartLine &&
      _fastrActive()
    ) {
      const cl = window.__mwCartLine;
      let currentQty = cl.quantity;
      for (const [, refs] of formRefs) {
        if (!refs.select) continue;
        currentQty = Math.round((parseFloat(refs.select.value) || MIN) / STEP);
        break;
      }
      const linePrice = cl.price * currentQty;
      const netLen = (() => {
        for (const [, refs] of formRefs) {
          if (!refs.select) continue;
          return (parseFloat(refs.select.value) || MIN).toFixed(1) + " m";
        }
        return cl.properties?.["Net length"] || "1.0 m";
      })();
      const fakeCart = {
        token: "mw-fake-token",
        note: null,
        attributes: {},
        original_total_price: linePrice,
        total_price: linePrice,
        total_discount: 0,
        total_weight: 0,
        item_count: currentQty,
        requires_shipping: true,
        currency: "INR",
        items_subtotal_price: linePrice,
        cart_level_discount_applications: [],
        items: [
          {
            id: cl.variant_id,
            properties: { "Net length": netLen },
            quantity: currentQty,
            variant_id: cl.variant_id,
            key: cl.variant_id + ":mw-fake",
            title: cl.title,
            price: cl.price,
            original_price: cl.price,
            discounted_price: cl.price,
            line_price: linePrice,
            original_line_price: linePrice,
            total_discount: 0,
            discounted_total_price: linePrice,
            product_id: cl.product_id,
            gift_card: false,
            final_price: cl.price,
            final_line_price: linePrice,
            url: window.location.pathname,
            image: "",
            handle: window.location.pathname.replace(/.*\/products\//, ""),
            requires_shipping: true,
            product_type: "",
            product_title: cl.title,
            product_description: "",
            variant_title: null,
            variant_options: ["Default Title"],
            options_with_values: [{ name: "Title", value: "Default Title" }],
            line_level_discount_allocations: [],
            line_level_total_discount: 0,
            sku: "",
            taxable: true,
            grams: 0,
            vendor: "",
            selling_plan_allocation: null,
          },
        ],
      };
      console.log(
        "[metre-selector] Fastr /cart.js faked → qty:",
        currentQty,
        "| unit price:",
        cl.price,
        "| line_price:",
        linePrice,
      );
      return Promise.resolve(
        new Response(JSON.stringify(fakeCart), {
          status: 200,
          headers: { "Content-Type": "application/json" },
        }),
      );
    }

    if (
      url.includes("/cart/add") &&
      init?.method?.toUpperCase() === "POST" &&
      init?.body
    ) {
      // ── Fastr's Buy Now /cart/add call — return fake 200 with correct qty/price ──
      // syncBuyNowCart uses _origFetch (bypasses this interceptor) and keeps the
      // Shopify cart correct. Fastr's own /cart/add would add duplicate qty or fail
      // with 422. Instead return a fake 200 so Fastr's checkout overlay shows the
      // right quantity and price.
      // __mwCartLine.price is the per-unit price (set by syncBuyNowCart).
      // We compute qty from the CURRENT metre dropdown so it's always up-to-date
      // even if the dropdown changed after the last sync.
      if (
        window.location.pathname.includes("/products/") &&
        window.__mwCartLine &&
        _fastrActive()
      ) {
        const cl = window.__mwCartLine;
        // Always use the live dropdown value — not the stale synced qty
        let currentQty = cl.quantity;
        for (const [, refs] of formRefs) {
          if (!refs.select) continue;
          currentQty = Math.round(
            (parseFloat(refs.select.value) || MIN) / STEP,
          );
          break;
        }
        const linePrice = cl.price * currentQty;
        const fakeItem = {
          id: cl.variant_id,
          variant_id: cl.variant_id,
          product_id: cl.product_id,
          quantity: currentQty,
          price: cl.price,
          original_price: cl.price,
          discounted_price: cl.price,
          line_price: linePrice,
          original_line_price: linePrice,
          total_discount: 0,
          discounted_total_price: linePrice,
          final_price: cl.price,
          final_line_price: linePrice,
          title: cl.title,
          url: window.location.pathname,
          properties: cl.properties || {},
          gift_card: false,
          taxable: true,
          grams: 0,
          vendor: "",
          sku: "",
          line_level_discount_allocations: [],
          line_level_total_discount: 0,
        };
        console.log(
          "[metre-selector] Fastr /cart/add faked → qty:",
          currentQty,
          "| unit price:",
          cl.price,
          "| line_price:",
          linePrice,
        );
        return Promise.resolve(
          new Response(JSON.stringify(fakeItem), {
            status: 200,
            headers: { "Content-Type": "application/json" },
          }),
        );
      }

      // ── Normal Add to Cart (not Fastr Buy Now) — modify qty for metre products ──
      // Prefer visible form (quick view open) over background form (product page)
      let refs = null;
      for (const [form, r] of formRefs) {
        if (form.offsetParent !== null) {
          refs = r;
          break;
        }
      }
      // Fallback: match by variant ID so a PCS form add never picks up a metre
      // form's refs when both happen to be in the DOM at the same time.
      if (!refs && formRefs.size > 0) {
        try {
          const bodyObj =
            typeof init.body === "string" ? JSON.parse(init.body) : null;
          const addVariant = String(bodyObj?.id || "");
          if (addVariant) {
            for (const [form, r] of formRefs) {
              const formVariant = String(
                form.querySelector('[name="id"]')?.value || "",
              );
              if (formVariant && formVariant === addVariant) {
                refs = r;
                break;
              }
            }
          }
        } catch (_) {}
      }

      const select = refs?.select;
      const netLen = refs?.netLen;

      if (select && netLen) {
        const metres = parseFloat(select.value) || MIN;
        const netLenVal = netLen.value || metres.toFixed(1) + " m";

        try {
          if (typeof init.body === "string") {
            const body = JSON.parse(init.body);
            body.quantity = Math.round(metres / STEP);
            if (!body.properties) body.properties = {};
            body.properties["Net length"] = netLenVal;
            init = { ...init, body: JSON.stringify(body) };
            console.log(
              `[metre-selector] Intercepted /cart/add (JSON) → qty=${Math.round(metres / STEP)}, Net length=${netLenVal}`,
            );
          } else if (init.body instanceof FormData) {
            init.body.set("quantity", String(Math.round(metres / STEP)));
            init.body.set("properties[Net length]", netLenVal);
            console.log(
              `[metre-selector] Intercepted /cart/add (FormData) → qty=1, Net length=${netLenVal}`,
            );
          }
        } catch (_) {
          /* parse error — let through unchanged */
        }
      }
    }
    return _origFetch.call(this, input, init, ...rest);
  };

  // ── Safe Buy Now wrapper callable from onclick ────────────────────────────
  // Tries every known Fastr/Shiprocket product-checkout entry point so the
  // button works regardless of which function name the theme exposes.
  window.mwBuyNow = function (event) {
    // 1. Update data-quantity and switch to mini-cart mode so buyCart reads
    //    from Shopify cart (syncBuyNowCart already pre-added the item there).
    let qty = null;
    for (const [, refs] of formRefs) {
      if (!refs.select) continue;
      qty = Math.round((parseFloat(refs.select.value) || MIN) / STEP);
      break;
    }
    document.querySelectorAll(".shiprocket-headless").forEach((el) => {
      el.setAttribute("data-type", "mini-cart");
      if (qty) el.setAttribute("data-quantity", String(qty));
    });

    // 2. Use buyCart — same as cart pages, reads from Shopify cart which is
    //    already pre-populated by syncBuyNowCart. This is the known-working path.
    if (typeof window.shiprocketCheckoutEvents?.buyCart === "function") {
      console.log("[metre-selector] mwBuyNow → shiprocketCheckoutEvents.buyCart ✓");
      return window.shiprocketCheckoutEvents.buyCart(event);
    }

    // 3. Fallbacks if buyCart unavailable
    if (typeof window.buyProduct === "function") {
      console.log("[metre-selector] mwBuyNow → buyProduct");
      return window.buyProduct(event);
    }
    if (typeof window.shiprocketCheckoutBuyProductHandler === "function") {
      console.log("[metre-selector] mwBuyNow → shiprocketCheckoutBuyProductHandler");
      return window.shiprocketCheckoutBuyProductHandler(event);
    }

    // Debug: log what Fastr has exposed
    console.warn("[metre-selector] mwBuyNow: no Fastr function found");
    const fastrFns = Object.getOwnPropertyNames(window).filter((k) =>
      /shiprocket|fastrr|pickrr|buyCart|buyProduct|checkout/i.test(k)
    );
    console.log("[metre-selector] Fastr globals:", fastrFns);
    if (window.shiprocketCheckoutEvents)
      console.log("[metre-selector] shiprocketCheckoutEvents methods:", Object.keys(window.shiprocketCheckoutEvents));
  };

  // ── Patch window.buyProduct via defineProperty (guaranteed regardless of load order) ──
  (function patchBuyProduct() {
    function _wrapBP(origFn) {
      return function (...args) {
        const [event] = args;
        let qty = null;
        for (const [, refs] of formRefs) {
          if (!refs.select) continue;
          qty = Math.round((parseFloat(refs.select.value) || MIN) / STEP);
          break;
        }
        console.log("[metre-selector] buyProduct called — correctQty:", qty);
        if (qty) {
          // Update data-quantity on .shiprocket-headless BEFORE buyProduct reads it
          document.querySelectorAll(".shiprocket-headless").forEach((el) => {
            el.setAttribute("data-quantity", String(qty));
          });
          // Also patch event target if it has data-quantity
          const el =
            event?.target?.closest?.("[data-quantity]") ||
            event?.target?.closest?.(".shiprocket-headless");
          if (el) el.setAttribute("data-quantity", String(qty));
          // Log dataset so we can see what Fastr reads
          const sh = document.querySelector(".shiprocket-headless");
          if (sh)
            console.log(
              "[metre-selector] .shiprocket-headless dataset:",
              JSON.stringify(sh.dataset),
            );
        }
        return origFn.apply(this, args);
      };
    }

    // Case A: buyProduct already defined (shopify.js loaded before our script)
    if (typeof window.buyProduct === "function") {
      window.buyProduct = _wrapBP(window.buyProduct);
      console.log("[metre-selector] window.buyProduct patched (immediate) ✓");
      return;
    }

    // Case B: intercept the assignment via defineProperty setter
    try {
      Object.defineProperty(window, "buyProduct", {
        configurable: true,
        set: function (fn) {
          const wrapped = _wrapBP(fn);
          Object.defineProperty(window, "buyProduct", {
            configurable: true,
            writable: true,
            value: wrapped,
          });
          console.log("[metre-selector] window.buyProduct patched (setter) ✓");
        },
      });
    } catch (e) {
      // Fallback: poll until defined
      const _t = setInterval(() => {
        if (
          typeof window.buyProduct === "function" &&
          !window.buyProduct.__mwBP
        ) {
          const w = _wrapBP(window.buyProduct);
          w.__mwBP = true;
          window.buyProduct = w;
          clearInterval(_t);
          console.log("[metre-selector] window.buyProduct patched (poll) ✓");
        }
      }, 200);
      setTimeout(() => clearInterval(_t), 10000);
    }
  })();

  // ── Buy Now: pre-sync Shopify cart + switch Fastr to cart-mode checkout ────
  // Problem: Fastr's data-type="product" Buy Now stores qty=1 internally and shows
  // that in checkout — our /cart/add interception doesn't affect what Fastr DISPLAYS.
  // Solution: pre-populate Shopify cart with the correct qty, then change the button
  // to data-type="cart" so Fastr reads from Shopify cart (which already shows correct
  // qty+price in cart-page checkout).

  // window.__mwCartLine = the Shopify line item after a successful sync.
  // Used by the /cart/add interceptor to return a fake 200 to Fastr.
  // All cart calls here use _origFetch (pre-patch) so they bypass our
  // interceptor and never race with Fastr's intercepted /cart/add calls.

  async function syncBuyNowCart(variantId, metres) {
    console.log(
      "[metre-selector] syncBuyNowCart — variantId:",
      variantId,
      "metres:",
      metres,
    );
    if (!variantId || !window.location.pathname.includes("/products/")) {
      console.log("[metre-selector] syncBuyNowCart — skipped");
      return;
    }
    const qty = Math.round(metres / STEP);
    const netLen = metres.toFixed(1) + " m";
    try {
      // _origFetch bypasses our interceptor — no race with Fastr's /cart/add
      const cart = await _origFetch
        .call(window, "/cart.js")
        .then((r) => r.json());

      // Remove any Fastr-added lines (no Net length property, same variant).
      // They push total qty over stock and cause /cart/change.js to 422.
      const fastrLines = cart.items.filter(
        (it) =>
          String(it.variant_id) === String(variantId) &&
          !it.properties?.["Net length"],
      );
      for (const fl of fastrLines) {
        const idx = cart.items.indexOf(fl);
        await _origFetch.call(window, "/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line: idx + 1, quantity: 0 }),
        });
      }

      // Re-read after cleanup (indices may have shifted)
      const freshCart = fastrLines.length
        ? await _origFetch.call(window, "/cart.js").then((r) => r.json())
        : cart;

      const lineIdx = freshCart.items.findIndex(
        (it) =>
          String(it.variant_id) === String(variantId) &&
          it.properties?.["Net length"],
      );

      let lineItem = null;
      if (lineIdx >= 0) {
        const res = await _origFetch.call(window, "/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            line: lineIdx + 1,
            quantity: qty,
            properties: { "Net length": netLen },
          }),
        });
        const updatedCart = await res.json();
        lineItem =
          updatedCart.items?.find(
            (it) =>
              String(it.variant_id) === String(variantId) &&
              it.properties?.["Net length"],
          ) || updatedCart.items?.[lineIdx];
      } else {
        const res = await _origFetch.call(window, "/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: qty,
            properties: { "Net length": netLen },
          }),
        });
        lineItem = await res.json();
      }

      // Store unit price; the interceptor computes live qty from the dropdown
      if (lineItem && (lineItem.variant_id || lineItem.id)) {
        window.__mwCartLine = {
          variant_id: lineItem.variant_id || lineItem.id,
          product_id: lineItem.product_id || 0,
          quantity: qty,
          price: lineItem.price || lineItem.final_price || 0,
          line_price: lineItem.final_line_price || lineItem.line_price || 0,
          title: lineItem.title || "",
          properties: lineItem.properties || { "Net length": netLen },
        };
        window.__mwCartSynced = true; // safe to let Buy Now click through now
        console.log(
          "[metre-selector] Buy Now cart synced: qty=" +
            qty +
            " | " +
            netLen +
            " | unit price:",
          window.__mwCartLine.price,
        );
      } else {
        // Sync produced no usable line item — still allow click (fake response handles display)
        window.__mwCartSynced = true;
        console.warn(
          "[metre-selector] syncBuyNowCart — unexpected response, __mwCartLine not updated:",
          lineItem,
        );
      }

      // Keep Fastr div in cart mode (may have been reset by Fastr's own logic)
      document.querySelectorAll(".shiprocket-headless").forEach((el) => {
        el.setAttribute("data-type", "cart");
      });
    } catch (err) {
      window.__mwCartSynced = true; // don't block click on error — fake response handles display
      console.error("[metre-selector] Buy Now cart sync failed:", err);
    }
  }

  // When Fastr creates its Buy Now div (data-type="product"), immediately:
  //   1. sync the Shopify cart with current metre selection
  //   2. switch the div to data-type="cart" after sync — Fastr's cart checkout
  //      reads /cart.js which has the correct qty and price
  function _checkFastrDiv() {
    document
      .querySelectorAll(
        '.shiprocket-headless[data-type="product"]:not([data-mw-syncing])',
      )
      .forEach((el) => {
        el.dataset.mwSyncing = "1";
        for (const [form, refs] of formRefs) {
          if (!refs.select) continue;
          const variantId = form.querySelector('[name="id"]')?.value;
          const metres = parseFloat(refs.select.value) || MIN;
          syncBuyNowCart(variantId, metres);
          break;
        }
      });
  }
  const _buyNowObserver = new MutationObserver(_checkFastrDiv);
  // childList catches new nodes; attributeFilter catches data-type changes on existing nodes
  _buyNowObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-type"],
  });

  // ── Buy Now click interceptor ──
  // Blocks the click until cart is synced to the current metre selection.
  // Fastr's cart-mode checkout (data-type="cart") reads from Shopify cart,
  // which syncBuyNowCart keeps updated. __mwCartSynced goes false on dropdown
  // change, true after sync — so if user clicks before sync finishes we wait.
  document.addEventListener(
    "click",
    async function _mwBuyNowCapture(e) {
      if (!window.location.pathname.includes("/products/")) return;
      const fastrEl = e.target.closest(".shiprocket-headless");
      if (!fastrEl) return;
      if (window.__mwRetriggeringClick) return; // our own re-fire — let through

      if (!window.__mwCartSynced) {
        e.preventDefault();
        e.stopImmediatePropagation();
        console.log(
          "[metre-selector] Buy Now click held — waiting for cart sync...",
        );
        let vid, metres;
        for (const [form, refs] of formRefs) {
          if (!refs.select) continue;
          vid = form.querySelector("[name='id']")?.value;
          metres = parseFloat(refs.select.value) || MIN;
          break;
        }
        if (vid) await syncBuyNowCart(vid, metres);
        // Re-fire the click — __mwRetriggeringClick prevents re-entry
        window.__mwRetriggeringClick = true;
        fastrEl.click();
        window.__mwRetriggeringClick = false;
      }
    },
    true,
  ); // capture phase — fires before Fastr's onclick

  // ── Globo collection page: redirect to product page instead of adding to cart ──
  if (!window.location.pathname.includes("/products/")) {
    document.addEventListener(
      "click",
      (e) => {
        const btn = e.target.closest(
          ".globo--add-to-cart, [data-globo-cart-bound]",
        );
        if (!btn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const card = btn.closest("[data-json-product]");
        let handle = null;
        if (card) {
          try {
            handle = JSON.parse(card.dataset.jsonProduct)?.handle;
          } catch (_) {}
        }
        const link = btn
          .closest(".product-item, li, article")
          ?.querySelector("a[href*='/products/']")
          ?.getAttribute("href");
        const dest = handle ? `/products/${handle}` : link;
        if (dest) window.location.href = dest;
      },
      true,
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
